import {
  MeshBuilder, Vector3, Color3, TransformNode, Mesh,
  ActionManager, ExecuteCodeAction, HighlightLayer,
  PointLight, DynamicTexture, PBRMaterial, VertexBuffer,
  VertexData, Matrix,
  SceneLoader,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import earcut from 'earcut';
import {
  BUILDING, BUILDING_SECTIONS, FOOTPRINT_OUTER, WALKWAY_OUTLINE,
  STAIR_OUTLINE, STAIR_NUM_STEPS,
  WALKWAY_PLANTERS,
  PARKING_GROUPS, PLACED_PLANTERS, PLACED_ROADS, GROUND_PAD_OUTLINE,
  CORE, GROUND_FLOOR, UPPER_FLOOR, BASEMENT, EXTERIOR, AMENITIES,
  ELEMENT_TYPES,
} from './building-data.js';
import { FurnitureFactory } from './furniture.js';
import fontData from './helvetiker_bold.json';

// Convert meters to feet — used for small architectural details (mullion sizes, offsets, etc.)
// that were originally designed in meters. Building coords are now natively in feet.
const m = (meters) => meters * 3.28084;

/** Seeded PRNG (mulberry32) — deterministic 0-1 values from integer seed. */
function seededRandom(seed) {
  let s = seed | 0;
  return function () {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Procedurally generates the Business Bay-2 building.
 */
export class BuildingGenerator {
  constructor(scene, materials) {
    this.scene = scene;
    this.mats = materials;
    this.floorNodes = {};
    this.unitMeshes = [];
    this.highlightLayer = new HighlightLayer('hl', scene);
    this.selectedMesh = null;
    this.onUnitSelect = null; // callback
    this.furniture = new FurnitureFactory(scene, materials);
  }

  generateAll() {
    this.scene.blockMaterialDirtyMechanism = true;

    this._createGround();
    this._createSidewalks();
    this._initMasterTrees();
    this._initGrass();
    this._createPlacedRoads();
    this._createExteriorShell();
    this._createAllFloors();
    this._createPlanters();
    this._createTrees();
    this._createTerraceGarden();
    this._createGroundPad();
    this._createSurfaceParking();
    this._createPlacedPlanters();
    // this._createWalkwayPlanters();
    this._flushGrass();

    this.scene.blockMaterialDirtyMechanism = false;

    // Freeze static meshes for performance
    this._freezeStaticMeshes();

    // Merge static meshes to reduce draw calls
    this._mergeStaticMeshes();

    // Load palm GLB asynchronously, then place all queued trees
    this._loadPalmModel().then(() => {
      this._flushPendingTrees();
    }).catch(err => {
      console.error('[BuildingGenerator] Failed to load palm GLB:', err);
    });

    return this.floorNodes;
  }

  _freezeStaticMeshes() {
    this.scene.meshes.forEach(m => {
      // Don't freeze clickable unit meshes (they need picking)
      if (m.name.includes('_click')) return;
      // Don't freeze floor-node children that change visibility
      if (m.parent && m.parent.name && m.parent.name.startsWith('floor_')) return;

      m.freezeWorldMatrix();
      m.doNotSyncBoundingInfo = true;
    });

    // Freeze all materials (except leaf alpha materials — freezing breaks opacityTexture)
    this.scene.materials.forEach(mat => {
      if (mat.name !== 'leavesAlpha' && mat.name !== 'leavesAlphaInner') {
        mat.freeze();
      }
    });
  }

  // ─── MESH MERGING (reduce draw calls for static geometry) ───

  /** Merge a group of meshes sharing the same material into one.
   *  Returns the merged mesh or null. */
  _mergeGroup(filter, mergedName, material, parentNode = null) {
    const arr = this.scene.meshes.filter(filter);
    if (arr.length < 2) return null;
    const merged = Mesh.MergeMeshes(arr, true, true, undefined, false, false);
    if (!merged) return null;
    merged.name = mergedName;
    merged.material = material;
    merged.isPickable = false;
    merged.doNotSyncBoundingInfo = true;
    merged.freezeWorldMatrix();
    if (parentNode) merged.parent = parentNode;
    return merged;
  }

  /** Merge static meshes that share materials to cut draw calls. */
  _mergeStaticMeshes() {
    const mats = this.mats;

    // Group 1 — Basement parking lines (per-floor)
    Object.entries(this.floorNodes).forEach(([key, node]) => {
      if (parseInt(key) >= 0) return; // basements only
      this._mergeGroup(
        m => m.name.startsWith('parkLine_' + key + '_') && m.material === mats.parkingLine,
        `parkLines_merged_B${Math.abs(parseInt(key))}`,
        mats.parkingLine,
        node,
      );
    });

    // Group 2 — Basement columns (per-floor)
    Object.entries(this.floorNodes).forEach(([key, node]) => {
      if (parseInt(key) >= 0) return;
      this._mergeGroup(
        m => m.name.startsWith('bCol_' + key + '_') && m.material === mats.concrete,
        `bCols_merged_B${Math.abs(parseInt(key))}`,
        mats.concrete,
        node,
      );
    });

    // Group 3 — Surface parking lines
    this._mergeGroup(
      m => m.name.startsWith('pLine_') && m.material === mats.parkingLine,
      'surfaceParkLines_merged',
      mats.parkingLine,
    );

    // Group 4 — Road center dashes
    this._mergeGroup(
      m => m.name.startsWith('placedRoadLine_') && m.material === mats.parkingLine,
      'roadDashes_merged',
      mats.parkingLine,
    );

    // Group 5 — Planter soil boxes (all green-top meshes using soil material)
    this._mergeGroup(
      m => (m.name.startsWith('planterGr_') || m.name.startsWith('roadMedGr_') ||
            m.name.startsWith('gapGr_') || m.name.startsWith('stairGr_')) && m.material === mats.soil,
      'soilBoxes_merged',
      mats.soil,
    );

    // Group 6 — Tree base discs
    this._mergeGroup(
      m => m.name.startsWith('treeBase_') && m.material === mats.bark,
      'treeBases_merged',
      mats.bark,
    );

    // Group 7 — Sidewalk strips (only scene-level ones, not floor-parented)
    this._mergeGroup(
      m => m.name.startsWith('sidewalk_') && m.material === mats.sidewalk &&
           !(m.parent && m.parent.name && m.parent.name.startsWith('floor_')),
      'sidewalks_merged',
      mats.sidewalk,
    );
  }

  // ─── FOOTPRINT HELPERS ───
  _getFootprintBounds() {
    if (this._fpBounds) return this._fpBounds;
    const xs = FOOTPRINT_OUTER.map(v => v.x);
    const zs = FOOTPRINT_OUTER.map(v => v.z);
    this._fpBounds = {
      minX: Math.min(...xs), maxX: Math.max(...xs),
      minZ: Math.min(...zs), maxZ: Math.max(...zs),
    };
    const b = this._fpBounds;
    b.w = b.maxX - b.minX;
    b.d = b.maxZ - b.minZ;
    b.cx = (b.minX + b.maxX) / 2;
    b.cz = (b.minZ + b.maxZ) / 2;
    return b;
  }

  /** Offset FOOTPRINT_OUTER outward by `overhang` feet to get canopy/fascia outline */
  _computeOffsetOutline(overhang) {
    const verts = FOOTPRINT_OUTER;
    const n = verts.length;
    const result = [];
    for (let i = 0; i < n; i++) {
      const prev = verts[(i - 1 + n) % n];
      const curr = verts[i];
      const next = verts[(i + 1) % n];
      const e1dx = curr.x - prev.x, e1dz = curr.z - prev.z;
      const e2dx = next.x - curr.x, e2dz = next.z - curr.z;
      const l1 = Math.hypot(e1dx, e1dz), l2 = Math.hypot(e2dx, e2dz);
      // Outward normals (right-normal for CCW polygon)
      const n1x = e1dz / l1, n1z = -e1dx / l1;
      const n2x = e2dz / l2, n2z = -e2dx / l2;
      const bx = n1x + n2x, bz = n1z + n2z;
      const bLen = Math.hypot(bx, bz);
      if (bLen < 0.001) {
        result.push({ x: curr.x + n1x * overhang, z: curr.z + n1z * overhang });
      } else {
        const bnx = bx / bLen, bnz = bz / bLen;
        const cosHalf = n1x * bnx + n1z * bnz;
        const d = overhang / Math.max(cosHalf, 0.65);
        result.push({ x: curr.x + bnx * d, z: curr.z + bnz * d });
      }
    }
    return result;
  }

  /** Ray-casting point-in-polygon test against FOOTPRINT_OUTER */
  _isInsideFootprint(px, pz) {
    const verts = FOOTPRINT_OUTER;
    let inside = false;
    const n = verts.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = verts[i].x, zi = verts[i].z;
      const xj = verts[j].x, zj = verts[j].z;
      if ((zi > pz) !== (zj > pz) && px < (xj - xi) * (pz - zi) / (zj - zi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  // ─── GROUND PLANE ───
  _createGround() {
    const fp = this._getFootprintBounds();
    const ground = MeshBuilder.CreateGround('ground', { width: 1000, height: 1000 }, this.scene);
    ground.position = new Vector3(fp.cx, -0.16, fp.cz);
    ground.material = this.mats.grass;
    ground.receiveShadows = true;
  }

  // ─── SIDEWALKS (around building perimeter, derived from FOOTPRINT_OUTER) ───
  _createSidewalks() {
    const swWidth = 10;
    FOOTPRINT_OUTER.forEach((a, i) => {
      const b = FOOTPRINT_OUTER[(i + 1) % FOOTPRINT_OUTER.length];
      const dx = b.x - a.x, dz = b.z - a.z;
      const edgeLen = Math.hypot(dx, dz);
      if (edgeLen < 1) return;
      // Skip diagonal edges (not axis-aligned)
      if (Math.abs(dx) > 0.5 && Math.abs(dz) > 0.5) return;
      const nx = dz / edgeLen, nz = -dx / edgeLen; // outward normal
      const mesh = MeshBuilder.CreateBox(`sidewalk_${i}`, {
        width: edgeLen, height: 0.15, depth: swWidth,
      }, this.scene);
      mesh.position = new Vector3(
        (a.x + b.x) / 2 + nx * swWidth / 2, 0.05,
        (a.z + b.z) / 2 + nz * swWidth / 2
      );
      mesh.rotation.y = Math.atan2(-dz, dx);
      mesh.material = this.mats.sidewalk;
    });
  }

  // ─── EXTERIOR SHELL ───
  _createExteriorShell() {
    const totalH = BUILDING.totalHeight;

    // Roof slab — polygon shape matching building footprint
    const roofPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
    const roofSlab = MeshBuilder.CreatePolygon('extSlab', {
      shape: roofPath, depth: m(0.3),
    }, this.scene, earcut);
    roofSlab.position.y = totalH;
    roofSlab.material = this.mats.wallExterior;

    // Glass curtain wall — continuous ribbon style
    this._createGlassFacade();

    // Roof canopy overhang
    this._createRoofCanopy();

    // "BUSINESS BAY 2" signage on roof
    this._createBuildingSignage();

    // Stone base plinth (warm stone at ground level)
    this._createStoneBase();

    // Showroom signage boards (above ground floor glass)
    this._createShowroomSignage();

    // Warm interior glow planes behind glass
    this._createWarmGlowBackplanes();

    // Ground floor structural columns (define double-height visually)
    this._createGroundFloorPillars();

    // Showroom divider columns along diagonal facade
    this._createShowroomDividerColumns();

    // Showroom door frames (aluminum bars within each showroom unit)
    this._createShowroomDoorFrames();

    // Elevated walkway (13ft wide at showroom floor level along diagonal)
    this._createElevatedWalkway();

    // Steps from road level to walkway/showroom floor (4ft elevation)
    this._createShowroomSteps();

    // Vertical louver fins on side faces (west, inner L-shape)
    this._createSideLouvers();

    // Main entrance indicator
    this._createEntrance();
  }

  // ─── CONTINUOUS RIBBON GLASS FACADE (optimized — merged mullions) ───

  /** Derive facade segments from the editable FOOTPRINT_OUTER polygon */
  _buildFacadeSegments() {
    const verts = FOOTPRINT_OUTER;
    const segs = [];
    for (let i = 0; i < verts.length; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % verts.length];
      const dx = b.x - a.x;
      const dz = b.z - a.z;

      if (Math.abs(dz) < 0.5 && Math.abs(dx) > 0.5) {
        // Horizontal (EW) edge
        // Outward normal z-component = -dx/|dx|; +1 → south-facing, -1 → north-facing
        const outwardSign = dx > 0 ? -1 : 1;
        segs.push({
          face: `e${i}`,
          start: Math.min(a.x, b.x),
          len: Math.abs(dx),
          pos: a.z,
          dir: 'EW',
          outwardSign,
        });
      } else if (Math.abs(dx) < 0.5 && Math.abs(dz) > 0.5) {
        // Vertical (NS) edge
        // Outward normal x-component = dz/|dz|; +1 → east-facing, -1 → west-facing
        const outwardSign = dz > 0 ? 1 : -1;
        segs.push({
          face: `e${i}`,
          start: Math.min(a.z, b.z),
          len: Math.abs(dz),
          pos: a.x,
          dir: 'NS',
          outwardSign,
        });
      } else {
        // Diagonal edge — include for glass facade
        const edgeLen = Math.hypot(dx, dz);
        if (edgeLen < 1) continue;
        const nx = dz / edgeLen, nz = -dx / edgeLen;
        segs.push({
          face: `e${i}`,
          dir: 'DIAG',
          start: 0,  // offset = distance along edge
          len: edgeLen,
          ax: a.x, az: a.z,
          dx, dz,
          nx, nz,
          mx: (a.x + b.x) / 2, mz: (a.z + b.z) / 2,
          rotY: Math.atan2(nx, nz),
          outwardSign: 1,
        });
      }
    }
    return segs;
  }

  /** Split the diagonal facade into per-showroom sub-segments (GF-01 to GF-14).
   *  Each sub-segment matches the front edge (vertex[2]→vertex[3]) of a polygon showroom.
   *  Returns an array of DIAG-like segment objects, one per showroom. */
  _getShowroomFacadeSubSegments(diagSeg) {
    // Polygon showrooms GF-01 to GF-14 — front edge is vertex[2]→vertex[3]
    const polyShowrooms = GROUND_FLOOR.showrooms.filter(u => u.vertices && u.vertices.length >= 3);
    // Sort from east to west along the diagonal (vertex[2].x descending — matches facade direction)
    const sorted = [...polyShowrooms].sort((a, b) => b.vertices[2].x - a.vertices[2].x);

    return sorted.map((unit, idx) => {
      const v2 = unit.vertices[2]; // front-right
      const v3 = unit.vertices[3]; // front-left
      const dx = v3.x - v2.x;
      const dz = v3.z - v2.z;
      const edgeLen = Math.hypot(dx, dz);
      const nx = dz / edgeLen, nz = -dx / edgeLen;
      return {
        face: `${diagSeg.face}_sr${idx}`,
        dir: 'DIAG',
        start: 0,
        len: edgeLen,
        ax: v2.x, az: v2.z,
        dx, dz,
        nx, nz,
        mx: (v2.x + v3.x) / 2,
        mz: (v2.z + v3.z) / 2,
        rotY: Math.atan2(nx, nz),
        outwardSign: 1,
        unitId: unit.id,
      };
    });
  }

  _createGlassFacade() {
    const mullionSpacing = m(3.0); // ~10ft spacing
    const facadeSegments = this._buildFacadeSegments();

    // Collect all mullion meshes for merging at the end
    const allMullions = [];

    Object.entries(BUILDING.floors).forEach(([floorKey, floorData]) => {
      if (floorData.type === 'parking') return;

      const elev = floorData.elevation;
      const h = floorData.height;
      const isGround = floorKey === '0';

      let glassBottom, glassTop;
      if (isGround) {
        glassBottom = elev + m(0.45);
        glassTop = elev + h - m(0.25);
      } else {
        glassBottom = elev + m(0.4);
        glassTop = elev + h - m(0.4);
      }
      const glassH = glassTop - glassBottom;

      facadeSegments.forEach((seg) => {
        const isSideWall = seg.dir === 'NS'; // E, W, W_inner are side/end walls

        // Side walls on ground floor: solid wall instead of glass
        if (isSideWall && isGround) {
          const name = `facade_${seg.face}_${floorKey}`;
          this._createSolidSideWall(name, seg, elev, h);
          return; // skip glass, mullions, etc. for this segment
        }

        // Ground floor DIAG: split into per-showroom sub-segments
        const isDiagGF = isGround && seg.dir === 'DIAG';
        const subSegs = isDiagGF ? this._getShowroomFacadeSubSegments(seg) : [seg];

        // DIAG GF: glass starts at showroom floor level (4ft above ground)
        const effGlassBottom = isDiagGF ? elev + 4 : glassBottom;
        const effGlassH = glassTop - effGlassBottom;

        // DIAG GF: single continuous glass panel for full diagonal (not per-showroom)
        if (isDiagGF) {
          this._createRibbonGlass(`facade_${seg.face}_${floorKey}`, seg, effGlassBottom, effGlassH, isGround);
        }

        subSegs.forEach((ss) => {
          const name = `facade_${ss.face}_${floorKey}`;

          // Glass ribbon — skip for DIAG GF (already created as single panel above)
          if (!isDiagGF) {
            this._createRibbonGlass(name, ss, effGlassBottom, effGlassH, isGround);
          }

          // Vertical mullions — collect for merge
          // Ground floor front faces: split into lower (thick showroom) and upper (thin mezzanine) zones
          const isGFfront = isGround && !isSideWall;
          const gfSeparatorY = 14;                     // 14ft — mezzanine floor level
          const gfSeparatorThickness = m(0.30);       // floor slab height (~1ft)
          const mullionCount = Math.floor(ss.len / mullionSpacing);

          if (isGFfront) {
            // Lower zone: thick showroom mullions (glassBottom to separator)
            const lowerH = gfSeparatorY - glassBottom;
            // Upper zone: thin mezzanine mullions (above separator slab to glassTop)
            const upperBottom = gfSeparatorY + gfSeparatorThickness;
            const upperH = glassTop - upperBottom;

            // Vertical mullions — skip for DIAG GF (showroom separators handle division)
            if (!isDiagGF) {
              for (let i = 0; i <= mullionCount; i++) {
                const offset = ss.start + i * mullionSpacing;
                if (offset > ss.start + ss.len) break;
                allMullions.push(this._createMullion(`${name}_mvL_${i}`, ss, offset, glassBottom, lowerH, m(0.08), m(0.08)));
                allMullions.push(this._createMullion(`${name}_mvU_${i}`, ss, offset, upperBottom, upperH, m(0.04), m(0.06)));
              }
            }

            // Horizontal mullions + floor separator:
            // For DIAG GF — created as CONTINUOUS bars after the sub-segment loop (not per-showroom)
            if (!isDiagGF) {
              allMullions.push(this._createHorizontalMullion(`${name}_mhB`, ss, glassBottom, m(0.08), m(0.08)));
              allMullions.push(this._createHorizontalMullion(`${name}_mhSep`, ss, gfSeparatorY, m(0.08), m(0.08)));
              allMullions.push(this._createHorizontalMullion(`${name}_mhU`, ss, upperBottom, m(0.04), m(0.06)));
              allMullions.push(this._createHorizontalMullion(`${name}_mhT`, ss, glassTop, m(0.04), m(0.06)));
              this._createGFSeparatorSlab(`${name}_gfSlab`, ss, gfSeparatorY, gfSeparatorThickness);
            }
          } else {
            for (let i = 0; i <= mullionCount; i++) {
              const offset = ss.start + i * mullionSpacing;
              if (offset > ss.start + ss.len) break;
              allMullions.push(this._createMullion(`${name}_mv_${i}`, ss, offset, glassBottom, glassH));
            }
            allMullions.push(this._createHorizontalMullion(`${name}_mhB`, ss, glassBottom));
            allMullions.push(this._createHorizontalMullion(`${name}_mhT`, ss, glassTop));
          }

          // Spandrel panels — ONLY on upper floors (strong horizontal bands)
          if (!isGround) {
            const isFirstUpper = floorKey === '2';
            if (isFirstUpper) {
              this._createSpandrelPanel(`${name}_spB`, ss, elev - m(0.25), m(0.65),
                this.mats.stoneBase);
            } else {
              this._createSpandrelPanel(`${name}_spB`, ss, elev, m(0.5));
            }
            this._createSpandrelPanel(`${name}_spT`, ss, glassTop, m(0.5));
          }
        });

        // DIAG GF: continuous horizontal bars + floor separator spanning full diagonal
        if (isDiagGF) {
          const _sepY = 14;
          const _sepThick = m(0.70);       // taller floor separator (~2.3ft visible band)
          const _upperBot = _sepY + _sepThick;
          const diagName = `facade_${seg.face}_${floorKey}`;
          // No bottom bar on diagonal — glass goes to ground
          // Separator level bars
          allMullions.push(this._createHorizontalMullion(`${diagName}_mhSep`, seg, _sepY, m(0.04), m(0.06)));
          allMullions.push(this._createHorizontalMullion(`${diagName}_mhU`, seg, _upperBot, m(0.04), m(0.06)));
          // Top bar
          allMullions.push(this._createHorizontalMullion(`${diagName}_mhT`, seg, glassTop, m(0.04), m(0.06)));
          // Continuous floor separator slab — taller
          this._createGFSeparatorSlab(`${diagName}_gfSlab`, seg, _sepY, _sepThick);
        }

        // Ground floor: signage slab behind glass handles the GF/2F junction visual
      });
    });

    // Merge all mullions into one mesh
    if (allMullions.length > 0) {
      const merged = Mesh.MergeMeshes(allMullions, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'facade_mullions_merged';
        merged.material = this.mats.darkAluminium;
        merged.freezeWorldMatrix();
        merged.doNotSyncBoundingInfo = true;
      }
    }
  }

  _createRibbonGlass(name, seg, bottomY, glassH, isGround = false) {
    const glass = MeshBuilder.CreatePlane(`${name}_glass`, {
      width: seg.len, height: glassH,
    }, this.scene);
    glass.material = isGround ? this.mats.glassShowroom : this.mats.glass;
    const s = seg.outwardSign;

    if (seg.dir === 'DIAG') {
      glass.position = new Vector3(
        seg.mx + seg.nx * m(0.05),
        bottomY + glassH / 2,
        seg.mz + seg.nz * m(0.05)
      );
      glass.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      glass.position = new Vector3(
        seg.start + seg.len / 2,
        bottomY + glassH / 2,
        seg.pos + s * m(0.05)
      );
      if (s < 0) glass.rotation.y = Math.PI; // north-facing
    } else {
      glass.position = new Vector3(
        seg.pos + s * m(0.05),
        bottomY + glassH / 2,
        seg.start + seg.len / 2
      );
      glass.rotation.y = s > 0 ? Math.PI / 2 : -Math.PI / 2;
    }
  }

  // Solid wall for side faces on ground floor (replaces glass)
  _createSolidSideWall(name, seg, elev, floorH) {
    const wallH = floorH;
    const wall = MeshBuilder.CreateBox(`${name}_sideWall`, {
      width: m(0.15), height: wallH, depth: seg.len,
    }, this.scene);
    wall.material = this.mats.stoneBase;
    const s = seg.outwardSign;

    // Side walls are always dir=NS
    wall.position = new Vector3(
      seg.pos + s * m(0.15) / 2,
      elev + wallH / 2,
      seg.start + seg.len / 2
    );
  }

  _createMullion(name, seg, offset, bottomY, glassH, w = m(0.04), d = m(0.06)) {
    const mullion = MeshBuilder.CreateBox(name, {
      width: w, height: glassH, depth: d,
    }, this.scene);
    mullion.material = this.mats.darkAluminium;
    const s = seg.outwardSign;

    if (seg.dir === 'DIAG') {
      // offset = distance along edge from start vertex
      const ux = seg.dx / seg.len, uz = seg.dz / seg.len;
      mullion.position = new Vector3(
        seg.ax + ux * offset + seg.nx * m(0.08),
        bottomY + glassH / 2,
        seg.az + uz * offset + seg.nz * m(0.08)
      );
      mullion.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      mullion.position = new Vector3(
        offset, bottomY + glassH / 2, seg.pos + s * m(0.08)
      );
    } else {
      mullion.position = new Vector3(
        seg.pos + s * m(0.08), bottomY + glassH / 2, offset
      );
      mullion.rotation.y = Math.PI / 2;
    }
    return mullion;
  }

  _createHorizontalMullion(name, seg, y, h = m(0.04), d = m(0.06)) {
    const hm = MeshBuilder.CreateBox(name, {
      width: seg.len, height: h, depth: d,
    }, this.scene);
    hm.material = this.mats.darkAluminium;
    const s = seg.outwardSign;

    if (seg.dir === 'DIAG') {
      hm.position = new Vector3(
        seg.mx + seg.nx * m(0.08), y, seg.mz + seg.nz * m(0.08)
      );
      hm.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      hm.position = new Vector3(
        seg.start + seg.len / 2, y, seg.pos + s * m(0.08)
      );
    } else {
      hm.position = new Vector3(
        seg.pos + s * m(0.08), y, seg.start + seg.len / 2
      );
      hm.rotation.y = Math.PI / 2;
    }
    return hm;
  }

  _createSpandrelPanel(name, seg, y, panelH, material) {
    const sp = MeshBuilder.CreateBox(name, {
      width: seg.len, height: panelH, depth: m(0.12),
    }, this.scene);
    sp.material = material || this.mats.spandrel;
    const s = seg.outwardSign;

    if (seg.dir === 'DIAG') {
      sp.position = new Vector3(
        seg.mx + seg.nx * m(0.02), y + panelH / 2, seg.mz + seg.nz * m(0.02)
      );
      sp.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      sp.position = new Vector3(
        seg.start + seg.len / 2, y + panelH / 2, seg.pos + s * m(0.02)
      );
    } else {
      sp.position = new Vector3(
        seg.pos + s * m(0.02), y + panelH / 2, seg.start + seg.len / 2
      );
      sp.rotation.y = Math.PI / 2;
    }
  }

  // Floor separator slab behind glass for GF showroom / mezzanine division
  _createGFSeparatorSlab(name, seg, y, slabH) {
    const slab = MeshBuilder.CreateBox(name, {
      width: seg.len, height: slabH, depth: m(0.25),
    }, this.scene);
    slab.material = this.mats.spandrel;
    const s = seg.outwardSign;

    if (seg.dir === 'DIAG') {
      slab.position = new Vector3(
        seg.mx - seg.nx * m(0.10),  // behind glass (inward from facade)
        y + slabH / 2,
        seg.mz - seg.nz * m(0.10)
      );
      slab.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      slab.position = new Vector3(
        seg.start + seg.len / 2,
        y + slabH / 2,
        seg.pos - s * m(0.10)   // behind glass (inward from facade)
      );
    } else {
      slab.position = new Vector3(
        seg.pos - s * m(0.10),    // behind glass (inward from facade)
        y + slabH / 2,
        seg.start + seg.len / 2
      );
      slab.rotation.y = Math.PI / 2;
    }
  }

  // ─── ROOF CANOPY (overhanging ~2.5m beyond facade) ───
  _createRoofCanopy() {
    const totalH = BUILDING.totalHeight;
    const overhang = m(2.5);

    // Compute offset outline for canopy overhang
    const offsetVerts = this._computeOffsetOutline(overhang);
    const canopyShape = offsetVerts.map(v => new Vector3(v.x, 0, v.z));

    // Main canopy slab (polygon shape matching building footprint + overhang)
    const canopyTop = MeshBuilder.CreatePolygon('roofCanopy_top', {
      shape: canopyShape, depth: m(0.25),
    }, this.scene, earcut);
    canopyTop.position.y = totalH + m(1.2);
    canopyTop.material = this.mats.wallExterior;

    // Canopy soffit (warm gold/bronze underside)
    const canopySoffit = MeshBuilder.CreatePolygon('roofCanopy_soffit', {
      shape: canopyShape, depth: m(0.01),
    }, this.scene, earcut);
    canopySoffit.position.y = totalH + m(1.05);
    canopySoffit.material = this.mats.canopySoffit;

    // Canopy fascia — one band per edge of offset outline
    const fasciaH = m(1.0);
    offsetVerts.forEach((a, i) => {
      const b = offsetVerts[(i + 1) % offsetVerts.length];
      const dx = b.x - a.x, dz = b.z - a.z;
      const edgeLen = Math.hypot(dx, dz);
      if (edgeLen < 1) return;
      const fascia = MeshBuilder.CreateBox(`canopyFascia_${i}`, {
        width: edgeLen, height: fasciaH, depth: m(0.15),
      }, this.scene);
      fascia.position = new Vector3(
        (a.x + b.x) / 2, totalH + m(0.45) + fasciaH / 2, (a.z + b.z) / 2
      );
      fascia.rotation.y = Math.atan2(-dz, dx);
      fascia.material = this.mats.wallExterior;
    });

    // Low parapet wall along each footprint edge
    FOOTPRINT_OUTER.forEach((a, i) => {
      const b = FOOTPRINT_OUTER[(i + 1) % FOOTPRINT_OUTER.length];
      const dx = b.x - a.x, dz = b.z - a.z;
      const edgeLen = Math.hypot(dx, dz);
      if (edgeLen < 1) return;
      const parapet = MeshBuilder.CreateBox(`parapet_${i}`, {
        width: edgeLen, height: m(0.6), depth: m(0.15),
      }, this.scene);
      parapet.position = new Vector3(
        (a.x + b.x) / 2, totalH + m(0.3), (a.z + b.z) / 2
      );
      parapet.rotation.y = Math.atan2(-dz, dx);
      parapet.material = this.mats.spandrel;
    });
  }

  // ─── BUILDING SIGNAGE ("BUSINESS BAY 2" — 3D extruded letters on canopy roof) ───
  _createBuildingSignage() {
    const totalH = BUILDING.totalHeight;
    const canopyTopY = totalH + m(1.2) + m(0.125);
    const text = 'BUSINESS BAY 2';
    const fontSize = m(1.5);
    const fontScale = fontSize / fontData.resolution;
    const extrudeDepth = m(0.96);
    const letterSpacing = m(0.24);

    const signMat = new PBRMaterial('signMat', this.scene);
    signMat.albedoColor = new Color3(1.0, 1.0, 1.0);
    signMat.metallic = 0.3;
    signMat.roughness = 0.1;
    signMat.emissiveColor = new Color3(0.15, 0.15, 0.15);
    signMat.backFaceCulling = false;
    signMat.twoSidedLighting = true;

    const signRoot = new TransformNode('signRoot', this.scene);

    // Compute total text width for centering
    let totalWidth = 0;
    for (const char of text) {
      const glyph = fontData.glyphs[char];
      totalWidth += (glyph ? glyph.ha : 250) * fontScale + letterSpacing;
    }
    totalWidth -= letterSpacing;

    // All characters use ExtrudePolygon from parsed font glyph outlines
    let xPos = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const glyph = fontData.glyphs[char];
      const advance = (glyph ? glyph.ha : 250) * fontScale;

      if (char === ' ' || !glyph || !glyph.o) {
        xPos += advance + letterSpacing;
        continue;
      }

      const contours = this._parseGlyphContours(glyph.o, fontScale);
      if (contours.length === 0) {
        xPos += advance + letterSpacing;
        continue;
      }

      const shape = contours[0];
      const holes = contours.length > 1 ? contours.slice(1) : undefined;

      const letter = MeshBuilder.ExtrudePolygon(`sign_${i}`, {
        shape,
        holes,
        depth: extrudeDepth,
      }, this.scene, earcut);

      if (letter) {
        letter.rotation.x = -Math.PI / 2;
        letter.position.x = xPos;
        letter.material = signMat;
        letter.parent = signRoot;
      }

      xPos += advance + letterSpacing;
    }

    // Position set by user via sign mover tool
    signRoot.position = new Vector3(115.03, 57.67, 349.61);
    signRoot.rotation.y = 0.5434;
  }

  // Parse font glyph outline into contours (arrays of Vector3 in XZ plane).
  // Font format: m x y, l x y, q endX endY cpX cpY, b endX endY cp1X cp1Y cp2X cp2Y
  _parseGlyphContours(outline, fontScale) {
    const contours = [];
    let current = [];
    const parts = outline.trim().split(/\s+/);
    let idx = 0;
    const steps = 6;

    while (idx < parts.length) {
      switch (parts[idx]) {
        case 'm': {
          if (current.length > 0) contours.push(current);
          current = [];
          const x = parseFloat(parts[idx + 1]) * fontScale;
          const z = parseFloat(parts[idx + 2]) * fontScale;
          current.push(new Vector3(x, 0, z));
          idx += 3;
          break;
        }
        case 'l': {
          const x = parseFloat(parts[idx + 1]) * fontScale;
          const z = parseFloat(parts[idx + 2]) * fontScale;
          current.push(new Vector3(x, 0, z));
          idx += 3;
          break;
        }
        case 'q': {
          const ex = parseFloat(parts[idx + 1]) * fontScale;
          const ez = parseFloat(parts[idx + 2]) * fontScale;
          const cpx = parseFloat(parts[idx + 3]) * fontScale;
          const cpz = parseFloat(parts[idx + 4]) * fontScale;
          const s0 = current[current.length - 1];
          for (let s = 1; s <= steps; s++) {
            const t = s / steps;
            const mt = 1 - t;
            current.push(new Vector3(
              mt * mt * s0.x + 2 * mt * t * cpx + t * t * ex, 0,
              mt * mt * s0.z + 2 * mt * t * cpz + t * t * ez
            ));
          }
          idx += 5;
          break;
        }
        case 'b': {
          const ex = parseFloat(parts[idx + 1]) * fontScale;
          const ez = parseFloat(parts[idx + 2]) * fontScale;
          const c1x = parseFloat(parts[idx + 3]) * fontScale;
          const c1z = parseFloat(parts[idx + 4]) * fontScale;
          const c2x = parseFloat(parts[idx + 5]) * fontScale;
          const c2z = parseFloat(parts[idx + 6]) * fontScale;
          const s0 = current[current.length - 1];
          for (let s = 1; s <= steps; s++) {
            const t = s / steps;
            const mt = 1 - t;
            current.push(new Vector3(
              mt*mt*mt*s0.x + 3*mt*mt*t*c1x + 3*mt*t*t*c2x + t*t*t*ex, 0,
              mt*mt*mt*s0.z + 3*mt*mt*t*c1z + 3*mt*t*t*c2z + t*t*t*ez
            ));
          }
          idx += 7;
          break;
        }
        default:
          idx++;
      }
    }

    if (current.length > 0) contours.push(current);
    return contours;
  }

  // ─── DARK BASE PLINTH (dark granite strip at ground level) ───
  _createStoneBase() {
    const baseH = m(0.45);
    const thinBaseH = m(0.10);  // thin base on diagonal (showroom facade)
    // Stone base along each footprint edge
    FOOTPRINT_OUTER.forEach((a, i) => {
      const b = FOOTPRINT_OUTER[(i + 1) % FOOTPRINT_OUTER.length];
      const dx = b.x - a.x, dz = b.z - a.z;
      const edgeLen = Math.hypot(dx, dz);
      if (edgeLen < 1) return;
      // Diagonal edge: skip stone base entirely (showroom glass goes to ground)
      const isDiag = Math.abs(dx) > 0.5 && Math.abs(dz) > 0.5;
      if (isDiag) return;
      const h = baseH;
      const nx = dz / edgeLen, nz = -dx / edgeLen;
      const offset = m(0.05);
      const base = MeshBuilder.CreateBox(`stoneBase_${i}`, {
        width: edgeLen, height: h, depth: m(0.15),
      }, this.scene);
      base.position = new Vector3(
        (a.x + b.x) / 2 + nx * offset, h / 2,
        (a.z + b.z) / 2 + nz * offset
      );
      base.rotation.y = Math.atan2(-dz, dx);
      base.material = this.mats.spandrel;
    });
  }

  // ─── SHOWROOM SIGNAGE BOARDS (at top of ground floor, below 2nd floor) ───
  _createShowroomSignage() {
    const groundH = BUILDING.floors['0'].height;
    const signBandH = m(0.55);
    const signBandY = groundH - m(0.25) - signBandH / 2;

    const brands = [
      'Salvatore Ferragamo', 'TEKTRONIX', 'ARMANI', 'PIONEER',
      'GUESS', 'GUCCI', 'LEE', 'VERSACE', 'PRADA',
      'H&M', 'ZARA', 'FOSSIL', 'TOMMY', 'RAYMOND', 'TITAN', 'SWAROVSKI'
    ];

    const facadeSegs = this._buildFacadeSegments();
    const diagSeg = facadeSegs.find(s => s.dir === 'DIAG');
    const ewSegs = facadeSegs.filter(s => s.dir === 'EW');

    // ── DIAG facade: one signage board per showroom, centered on each ──
    let diagBrandCount = 0;
    if (diagSeg) {
      const subSegs = this._getShowroomFacadeSubSegments(diagSeg);
      diagBrandCount = Math.min(brands.length, subSegs.length);

      subSegs.forEach((ss, i) => {
        const brand = brands[i] || '';
        if (!brand) return;

        // Individual signage board per showroom
        const band = MeshBuilder.CreateBox(`signBand_diag_${i}`, {
          width: ss.len, height: signBandH, depth: m(0.05),
        }, this.scene);
        band.position = new Vector3(
          ss.mx - ss.nx * m(0.10), signBandY,
          ss.mz - ss.nz * m(0.10)
        );
        band.rotation.y = ss.rotY;
        band.material = this.mats.signageBoard;

        // One brand name centered on this showroom
        this._createBrandTextStrip(`diag_sr${i}`, [brand], ss, signBandH, signBandY);
      });
    }

    // ── EW segments: remaining brands ──
    const ewBrands = brands.slice(diagBrandCount);
    let ewIdx = 0;
    const ewTotalLen = ewSegs.reduce((sum, s) => sum + s.len, 0);

    ewSegs.forEach((seg, si) => {
      const s = seg.outwardSign;
      const band = MeshBuilder.CreateBox(`signBand_ew_${si}`, {
        width: seg.len, height: signBandH, depth: m(0.05),
      }, this.scene);
      band.position = new Vector3(
        seg.start + seg.len / 2, signBandY,
        seg.pos - s * m(0.10)
      );
      band.material = this.mats.signageBoard;

      const segCount = Math.max(1, Math.round(ewBrands.length * seg.len / ewTotalLen));
      const segBrandList = ewBrands.slice(ewIdx, ewIdx + segCount);
      ewIdx += segCount;
      if (segBrandList.length > 0) {
        this._createBrandTextStrip(`ew_${si}`, segBrandList, seg, signBandH, signBandY);
      }
    });
  }

  _createBrandTextStrip(face, brands, seg, bandH, bandY) {
    const faceLen = seg.len;
    const spacing = faceLen / brands.length;
    const texW = 16384;
    const texH = Math.max(64, Math.round(texW * bandH / faceLen));
    const tex = new DynamicTexture(`brandTex_${face}`, { width: texW, height: texH }, this.scene, true);
    const ctx = tex.getContext();
    ctx.clearRect(0, 0, texW, texH);

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const pxPerM = texW / faceLen;
    const slotW = spacing * pxPerM;
    const baseFontSize = Math.floor(texH * 0.7);

    brands.forEach((brand, i) => {
      let fontSize = baseFontSize;
      ctx.font = `bold ${fontSize}px Arial`;
      let tw = ctx.measureText(brand).width;
      const maxW = slotW * 0.85;
      if (tw > maxW) {
        fontSize = Math.floor(fontSize * (maxW / tw));
        ctx.font = `bold ${fontSize}px Arial`;
      }
      const cx = (i * spacing + spacing / 2) * pxPerM;
      ctx.fillText(brand, cx, texH / 2);
    });

    tex.update();
    tex.hasAlpha = true;

    const mat = new PBRMaterial(`brandTextMat_${face}`, this.scene);
    mat.albedoTexture = tex;
    mat.opacityTexture = tex;
    mat.emissiveColor = new Color3(1.0, 0.95, 0.85);
    mat.emissiveTexture = tex;
    mat.metallic = 0;
    mat.roughness = 0.5;
    mat.transparencyMode = 2;

    const plane = MeshBuilder.CreatePlane(`brandText_${face}`, {
      width: faceLen, height: bandH,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);
    plane.material = mat;

    const s = seg.outwardSign;
    if (seg.dir === 'DIAG') {
      plane.position = new Vector3(
        seg.mx + seg.nx * m(0.06), bandY, seg.mz + seg.nz * m(0.06)
      );
      plane.rotation.y = seg.rotY;
    } else if (seg.dir === 'EW') {
      plane.position = new Vector3(seg.start + faceLen / 2, bandY, seg.pos + s * m(0.06));
      if (s < 0) plane.rotation.y = Math.PI;
    } else {
      plane.position = new Vector3(seg.pos + s * m(0.06), bandY, seg.start + faceLen / 2);
      plane.rotation.y = s > 0 ? Math.PI / 2 : -Math.PI / 2;
    }
  }

  // ─── GROUND FLOOR DARK FRAME (thick dark columns + beam — matches reference) ───
  _createGroundFloorPillars() {
    const groundH = BUILDING.floors['0'].height;
    const colW = m(0.30);
    const colD = m(0.20);
    const colH = groundH - m(0.1);
    const pillars = [];
    const spacing = m(5.5);

    // Place pillars along each EW and DIAG facade segment (glass faces)
    const facadeSegs = this._buildFacadeSegments();
    facadeSegs.filter(s => s.dir === 'EW').forEach(seg => {
      const s = seg.outwardSign;
      for (let x = seg.start + spacing; x <= seg.start + seg.len - m(5); x += spacing) {
        const p = MeshBuilder.CreateBox(`gfp_${seg.face}_${x}`, {
          width: colW, height: colH, depth: colD,
        }, this.scene);
        p.position = new Vector3(x, colH / 2 + m(0.05), seg.pos + s * m(0.12));
        p.material = this.mats.darkAluminium;
        pillars.push(p);
      }
    });
    // Diagonal facade: skip regular-spaced pillars — showroom divider columns
    // (_createShowroomDividerColumns) handle this, aligned to actual showroom walls.

    if (pillars.length > 0) {
      const merged = Mesh.MergeMeshes(pillars, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'gf_dark_frame_merged';
        merged.material = this.mats.darkAluminium;
        merged.freezeWorldMatrix();
      }
    }
  }

  // ─── SHOWROOM SEPARATORS (diagonal GF) — vertical dividers between showrooms ───
  _createShowroomDividerColumns() {
    const diagSeg = this._buildFacadeSegments().find(s => s.dir === 'DIAG');
    if (!diagSeg) return;
    const subSegs = this._getShowroomFacadeSubSegments(diagSeg);
    if (!subSegs || subSegs.length < 2) return;

    const floorData = BUILDING.floors['0'];
    const elev = floorData.elevation;
    const h = floorData.height;
    const glassTop = elev + h - m(0.25);
    const fullH = glassTop - elev;  // from ground level to glass top

    // Normal and rotation from the diagonal (all sub-segs share the same)
    const ref = subSegs[0];
    const nx = ref.nx, nz = ref.nz, rotY = ref.rotY;

    // Collect boundary points (N+1 for N showrooms)
    const bPts = [];
    bPts.push({ x: subSegs[0].ax, z: subSegs[0].az }); // east endpoint
    subSegs.forEach(ss => {
      bPts.push({ x: ss.ax + ss.dx, z: ss.az + ss.dz }); // end of each sub-seg
    });

    // Showroom separators — gray vertical bars at each boundary
    const sepMeshes = [];
    const sepW = 0.5;    // 0.5ft wide along facade (subtle but visible)
    const sepD = 0.4;    // 0.4ft deep
    bPts.forEach((pt, i) => {
      const isEndpoint = (i === 0 || i === bPts.length - 1);
      const w = isEndpoint ? 0.8 : sepW;   // endpoints slightly wider
      const d = isEndpoint ? 0.5 : sepD;

      const sep = MeshBuilder.CreateBox(`sr_sep_${i}`, {
        width: w, height: fullH, depth: d,
      }, this.scene);
      sep.position = new Vector3(
        pt.x + nx * m(0.05),
        elev + fullH / 2,
        pt.z + nz * m(0.05)
      );
      sep.rotation.y = rotY;
      sep.material = this.mats.wallExterior;  // light gray — visible but not overpowering
      sepMeshes.push(sep);
    });
    if (sepMeshes.length > 0) {
      const merged = Mesh.MergeMeshes(sepMeshes, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'sr_separators_merged';
        merged.material = this.mats.wallExterior;
      }
    }
  }

  // ─── SHOWROOM DOOR FRAMES (aluminum bars within each showroom unit) ───
  _createShowroomDoorFrames() {
    const diagSeg = this._buildFacadeSegments().find(s => s.dir === 'DIAG');
    if (!diagSeg) return;
    const subSegs = this._getShowroomFacadeSubSegments(diagSeg);
    if (!subSegs || subSegs.length === 0) return;

    const frameMeshes = [];
    const handleMeshes = [];

    // Key elevations (ft)
    const lowerBot = 4;             // glass bottom
    const transomY = 11.5;          // horizontal transom bar
    const upperBot = 14 + m(0.70);  // upper zone bottom (~16.30 ft)
    const signBot = BUILDING.floors['0'].height - m(0.25) - m(0.55); // signage board bottom (~21.2 ft)
    const upperTop = signBot;        // extend up to touch the horizontal bar under signage

    const upperH = upperTop - upperBot;     // spans from upper zone bottom to signage bar
    const doorH = transomY - lowerBot;      // 7.5 ft

    subSegs.forEach((ss, sIdx) => {
      const ux = ss.dx / ss.len, uz = ss.dz / ss.len;

      // A0. Horizontal bar under signage board
      const signBar = MeshBuilder.CreateBox(`sr_df_hsb_${sIdx}`, {
        width: ss.len, height: m(0.04), depth: m(0.05),
      }, this.scene);
      signBar.position = new Vector3(
        ss.mx + ss.nx * m(0.08),
        signBot,
        ss.mz + ss.nz * m(0.08)
      );
      signBar.rotation.y = ss.rotY;
      frameMeshes.push(signBar);

      // A. Upper zone — 3 equally spaced thin vertical bars (at 1/4, 2/4, 3/4)
      for (let i = 1; i <= 3; i++) {
        const off = (i / 4) * ss.len;
        const bar = MeshBuilder.CreateBox(`sr_df_uv_${sIdx}_${i}`, {
          width: m(0.06), height: upperH, depth: m(0.06),
        }, this.scene);
        bar.position = new Vector3(
          ss.ax + ux * off + ss.nx * m(0.08),
          upperBot + upperH / 2,
          ss.az + uz * off + ss.nz * m(0.08)
        );
        bar.rotation.y = ss.rotY;
        frameMeshes.push(bar);
      }

      // B. Lower zone — 1 horizontal transom bar
      const transom = MeshBuilder.CreateBox(`sr_df_ht_${sIdx}`, {
        width: ss.len, height: m(0.06), depth: m(0.06),
      }, this.scene);
      transom.position = new Vector3(
        ss.mx + ss.nx * m(0.08),
        transomY,
        ss.mz + ss.nz * m(0.08)
      );
      transom.rotation.y = ss.rotY;
      frameMeshes.push(transom);

      // C. Lower zone — 2 vertical door-frame bars (22% and 78% of width)
      [0.22, 0.78].forEach((frac, bIdx) => {
        const off = frac * ss.len;
        const bar = MeshBuilder.CreateBox(`sr_df_dv_${sIdx}_${bIdx}`, {
          width: m(0.06), height: doorH, depth: m(0.06),
        }, this.scene);
        bar.position = new Vector3(
          ss.ax + ux * off + ss.nx * m(0.08),
          lowerBot + doorH / 2,
          ss.az + uz * off + ss.nz * m(0.08)
        );
        bar.rotation.y = ss.rotY;
        frameMeshes.push(bar);
      });

      // D. Center door seam — slim vertical strip at 50% (between left & right door)
      const centerOff = 0.50 * ss.len;
      const divider = MeshBuilder.CreateBox(`sr_df_div_${sIdx}`, {
        width: m(0.025), height: doorH, depth: m(0.025),
      }, this.scene);
      divider.position = new Vector3(
        ss.ax + ux * centerOff + ss.nx * m(0.085),
        lowerBot + doorH / 2,
        ss.az + uz * centerOff + ss.nz * m(0.085)
      );
      divider.rotation.y = ss.rotY;
      handleMeshes.push(divider);

      // E. Door handles — rounded chrome pull handles (cylinders)
      const handleGrabH = 2.5;
      const handleCenterY = 8.0;
      const handleGrabBot = handleCenterY - handleGrabH / 2;
      const handleR = m(0.02);   // grab bar radius
      const nGlass = m(0.09);
      const nGrab = m(0.16);     // closer to door
      const nBracket = (nGlass + nGrab) / 2;
      const bracketLen = nGrab - nGlass;

      [0.44, 0.56].forEach((frac, hIdx) => {
        const off = frac * ss.len;
        const baseX = ss.ax + ux * off;
        const baseZ = ss.az + uz * off;

        // Vertical grab bar (capsule — rounded ends)
        const grab = MeshBuilder.CreateCapsule(`sr_df_hg_${sIdx}_${hIdx}`, {
          radius: handleR, height: handleGrabH, tessellation: 12, subdivisions: 1,
        }, this.scene);
        grab.position = new Vector3(
          baseX + ss.nx * nGrab, handleCenterY, baseZ + ss.nz * nGrab
        );
        handleMeshes.push(grab);

        // Top mounting bracket (horizontal capsule)
        const topB = MeshBuilder.CreateCapsule(`sr_df_htb_${sIdx}_${hIdx}`, {
          radius: handleR * 0.75, height: bracketLen, tessellation: 10, subdivisions: 1,
        }, this.scene);
        topB.position = new Vector3(
          baseX + ss.nx * nBracket,
          handleGrabBot + handleGrabH,
          baseZ + ss.nz * nBracket
        );
        topB.rotation.y = ss.rotY;
        topB.rotation.x = Math.PI / 2;
        handleMeshes.push(topB);

        // Bottom mounting bracket (horizontal capsule)
        const botB = MeshBuilder.CreateCapsule(`sr_df_hbb_${sIdx}_${hIdx}`, {
          radius: handleR * 0.75, height: bracketLen, tessellation: 10, subdivisions: 1,
        }, this.scene);
        botB.position = new Vector3(
          baseX + ss.nx * nBracket,
          handleGrabBot,
          baseZ + ss.nz * nBracket
        );
        botB.rotation.y = ss.rotY;
        botB.rotation.x = Math.PI / 2;
        handleMeshes.push(botB);
      });
    });

    // Merge frame bars (dark aluminium)
    if (frameMeshes.length > 0) {
      const merged = Mesh.MergeMeshes(frameMeshes, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'sr_doorframes_merged';
        merged.material = this.mats.darkAluminium;
        merged.freezeWorldMatrix();
        merged.doNotSyncBoundingInfo = true;
      }
    }
    // Merge handles separately (stainless steel / glossy chrome)
    if (handleMeshes.length > 0) {
      const merged = Mesh.MergeMeshes(handleMeshes, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'sr_doorhandles_merged';
        merged.material = this.mats.chrome;
        merged.freezeWorldMatrix();
        merged.doNotSyncBoundingInfo = true;
      }
    }
  }

  // ─── ELEVATED WALKWAY (building platform + 13ft walkway along diagonal) ───
  _createElevatedWalkway() {
    const platformH = 4;

    // 1) Combined platform+walkway as single merged polygon to fill gap
    //    Merge FOOTPRINT_OUTER and WALKWAY_OUTLINE outer vertices into one shape
    if (WALKWAY_OUTLINE.length >= 3) {
      // Find the two walkway inner vertices that sit on the footprint diagonal (V6, V7)
      // WALKWAY_OUTLINE[0] = V6 (289, 241.5), WALKWAY_OUTLINE[1] = V7 (118.5, 344.5)
      // WALKWAY_OUTLINE[2] = outer north, WALKWAY_OUTLINE[3] = outer south
      // Build expanded footprint: replace V6→V7 edge with V6→outerSouth→outerNorth→V7
      const fp = FOOTPRINT_OUTER;
      const wk = WALKWAY_OUTLINE;

      // Find which footprint vertices match walkway inner edge (V6, V7)
      const v6Idx = fp.findIndex(v => Math.abs(v.x - wk[0].x) < 1 && Math.abs(v.z - wk[0].z) < 1);
      const v7Idx = fp.findIndex(v => Math.abs(v.x - wk[1].x) < 1 && Math.abs(v.z - wk[1].z) < 1);

      if (v6Idx >= 0 && v7Idx >= 0) {
        // Build combined shape: footprint vertices up to V6, then walkway outer vertices, then continue from V7
        const combined = [];
        // Walk footprint from V7+1 around to V6 (inclusive)
        const n = fp.length;
        let idx = (v7Idx + 1) % n;
        while (idx !== v6Idx) {
          combined.push(fp[idx]);
          idx = (idx + 1) % n;
        }
        combined.push(fp[v6Idx]); // V6

        // Walkway outer: from V6 side outward → outer south → outer north → back to V7
        combined.push(wk[3]); // outer south (near V6)
        combined.push(wk[2]); // outer north (near V7)
        combined.push(fp[v7Idx]); // V7

        const combinedPath = combined.map(v => new Vector3(v.x, 0, v.z));
        const platform = MeshBuilder.CreatePolygon('building_platform', {
          shape: combinedPath,
          depth: platformH,
        }, this.scene, earcut);
        platform.position.y = platformH;
        platform.material = this.mats.grayMarble;
        this._setTriplanarWorldUVs(platform);
      } else {
        // Fallback: separate polygons
        const footprintPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
        const platform = MeshBuilder.CreatePolygon('building_platform', {
          shape: footprintPath, depth: platformH,
        }, this.scene, earcut);
        platform.position.y = platformH;
        platform.material = this.mats.grayMarble;
        this._setTriplanarWorldUVs(platform);

        const walkwayPath = WALKWAY_OUTLINE.map(v => new Vector3(v.x, 0, v.z));
        const walkway = MeshBuilder.CreatePolygon('walkway_strip', {
          shape: walkwayPath, depth: platformH,
        }, this.scene, earcut);
        walkway.position.y = platformH;
        walkway.material = this.mats.sidewalk;
      }
    } else {
      // No walkway defined — just footprint
      const footprintPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
      const platform = MeshBuilder.CreatePolygon('building_platform', {
        shape: footprintPath, depth: platformH,
      }, this.scene, earcut);
      platform.position.y = platformH;
      platform.material = this.mats.grayMarble;
      this._setTriplanarWorldUVs(platform);
    }
  }

  // ─── SHOWROOM STEPS (road level to walkway along diagonal facade) ───
  _createShowroomSteps() {
    if (STAIR_OUTLINE.length < 4) return;

    const platformH = 4;
    const numSteps = STAIR_NUM_STEPS;
    const stepH = platformH / numSteps;

    // STAIR_OUTLINE: [0]=innerSouth, [1]=innerNorth, [2]=outerNorth, [3]=outerSouth
    const iS = STAIR_OUTLINE[0], iN = STAIR_OUTLINE[1];
    const oN = STAIR_OUTLINE[2], oS = STAIR_OUTLINE[3];

    // Small overlap fraction to eliminate seams between steps
    const depthS = Math.hypot(oS.x - iS.x, oS.z - iS.z);
    const overlapFrac = depthS > 0 ? 0.1 / depthS : 0;

    // Interpolate a point along the inner→outer edge at fraction t
    const lerpSide = (inner, outer, t) => ({
      x: inner.x + (outer.x - inner.x) * t,
      z: inner.z + (outer.z - inner.z) * t,
    });

    const stepMeshes = [];
    for (let s = 0; s < numSteps; s++) {
      const topY = platformH - s * stepH;
      const t0 = s / numSteps;
      const t1 = Math.min(1, (s + 1) / numSteps + overlapFrac);

      // Parallelogram footprint with actual outline side edges (CCW from +Y)
      const sO = lerpSide(iS, oS, t1); // south-outer
      const nO = lerpSide(iN, oN, t1); // north-outer
      const nI = lerpSide(iN, oN, t0); // north-inner
      const sI = lerpSide(iS, oS, t0); // south-inner

      const shape = [
        new Vector3(sO.x, 0, sO.z),
        new Vector3(nO.x, 0, nO.z),
        new Vector3(nI.x, 0, nI.z),
        new Vector3(sI.x, 0, sI.z),
      ];

      const step = MeshBuilder.CreatePolygon(`sr_step_${s}`, {
        shape: shape,
        depth: topY,
      }, this.scene, earcut);

      // CreatePolygon builds top at y=0, extrudes to y=-depth; shift up
      step.position.y = topY;
      step.material = this.mats.grayMarble;
      stepMeshes.push(step);
    }

    if (stepMeshes.length > 0) {
      const merged = Mesh.MergeMeshes(stepMeshes, true, true, undefined, false, true);
      if (merged) {
        merged.name = 'sr_steps_merged';
        merged.material = this.mats.grayMarble;
        this._setTriplanarWorldUVs(merged);
      }
    }
  }

  // ─── VERTICAL LOUVER FINS (west side facade — subtle sun-shading) ───
  _createSideLouvers() {
    // West side louvers removed — no side louvers currently
  }

  // ─── MAIN ENTRANCE INDICATOR ───
  _createEntrance() {
    // Entrance is indicated by the glass facade on the diagonal south face.
    // No additional protruding elements needed.
  }

  // ─── WARM GLOW BACKPLANES (interior light behind glass) ───
  _createWarmGlowBackplanes() {
    const facadeSegs = this._buildFacadeSegments();
    Object.entries(BUILDING.floors).forEach(([floorKey, floorData]) => {
      if (floorData.type === 'parking') return;
      const elev = floorData.elevation;
      const h = floorData.height;
      const midY = elev + h / 2;
      const isGround = floorKey === '0';

      facadeSegs.forEach((seg, si) => {
        // Ground floor NS edges are solid walls — no glow
        if (isGround && seg.dir === 'NS') return;

        // Ground floor DIAG: per-showroom glow planes
        if (isGround && seg.dir === 'DIAG') {
          const subSegs = this._getShowroomFacadeSubSegments(seg);
          subSegs.forEach((ss, ssi) => {
            const glow = MeshBuilder.CreatePlane(`glow_${si}_sr${ssi}_${floorKey}`, {
              width: ss.len - m(0.5), height: h - m(0.8),
            }, this.scene);
            glow.position = new Vector3(
              ss.mx - ss.nx * m(0.5), midY, ss.mz - ss.nz * m(0.5)
            );
            glow.rotation.y = ss.rotY;
            glow.material = this.mats.warmGlow;
          });
          return;
        }

        const s = seg.outwardSign;
        const glow = MeshBuilder.CreatePlane(`glow_${si}_${floorKey}`, {
          width: seg.len - m(2), height: h - m(0.8),
        }, this.scene);
        if (seg.dir === 'DIAG') {
          glow.position = new Vector3(
            seg.mx - seg.nx * m(0.5), midY, seg.mz - seg.nz * m(0.5)
          );
          glow.rotation.y = seg.rotY;
        } else if (seg.dir === 'EW') {
          glow.position = new Vector3(seg.start + seg.len / 2, midY, seg.pos - s * m(0.5));
          if (s < 0) glow.rotation.y = Math.PI;
        } else {
          glow.position = new Vector3(seg.pos - s * m(0.5), midY, seg.start + seg.len / 2);
          glow.rotation.y = s > 0 ? Math.PI / 2 : -Math.PI / 2;
        }
        glow.material = this.mats.warmGlow;
      });
    });
  }

  // ─── ALL FLOORS ───
  _createAllFloors() {
    Object.entries(BUILDING.floors).forEach(([key, data]) => {
      const node = new TransformNode(`floor_${key}`, this.scene);
      this.floorNodes[key] = node;

      switch (data.type) {
        case 'parking':
          this._createBasementFloor(key, data, node);
          break;
        case 'showroom':
          this._createGroundFloor(data, node);
          break;
        case 'mixed':
          this._createUpperFloor(key, data, node);
          break;
      }
    });
  }

  // ─── BASEMENT ───
  _createBasementFloor(key, floorData, parent) {
    const elev = floorData.elevation;
    const h = floorData.height;

    // Floor slab — polygon matching building footprint
    const bFloorPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
    const bFloor = MeshBuilder.CreatePolygon(`bFloor_${key}`, {
      shape: bFloorPath, depth: m(0.2),
    }, this.scene, earcut);
    bFloor.position.y = elev;
    bFloor.material = this.mats.concrete;
    bFloor.parent = parent;

    // Ceiling
    const bCeil = MeshBuilder.CreatePolygon(`bCeil_${key}`, {
      shape: bFloorPath, depth: m(0.2),
    }, this.scene, earcut);
    bCeil.position.y = elev + h;
    bCeil.material = this.mats.concrete;
    bCeil.parent = parent;

    // Structural columns (grid pattern)
    const fp = this._getFootprintBounds();
    for (let x = fp.minX + m(4); x < fp.maxX - m(2); x += m(8)) {
      for (let z = fp.minZ + m(4); z < fp.maxZ - m(2); z += m(8)) {
        const col = MeshBuilder.CreateBox(`bCol_${key}_${x}_${z}`, {
          width: m(0.5), height: h, depth: m(0.5),
        }, this.scene);
        col.position = new Vector3(x, elev + h / 2, z);
        col.material = this.mats.concrete;
        col.parent = parent;
      }
    }

    // Parking slot lines
    BASEMENT.parkingRows.forEach((row, ri) => {
      const slotW = row.w / row.slots;
      for (let i = 0; i < row.slots; i++) {
        const line = MeshBuilder.CreateBox(`parkLine_${key}_${ri}_${i}`, {
          width: m(0.05), height: m(0.02), depth: row.d - m(0.5),
        }, this.scene);
        line.position = new Vector3(
          row.x + i * slotW, elev + m(0.11), row.z + row.d / 2
        );
        line.material = this.mats.parkingLine;
        line.parent = parent;
      }
    });

    // Driveways (slightly different color)
    BASEMENT.driveways.forEach((dw, di) => {
      const mesh = MeshBuilder.CreateBox(`driveway_${key}_${di}`, {
        width: dw.w, height: m(0.02), depth: dw.d,
      }, this.scene);
      mesh.position = new Vector3(dw.x + dw.w / 2, elev + m(0.12), dw.z + dw.d / 2);
      mesh.material = this.mats.sidewalk;
      mesh.parent = parent;
    });

    // Conference hall (Basement 01 only)
    if (key === '-1') {
      const conf = BASEMENT.conferenceHall;
      this._createRoom(`confHall`, conf.x, elev, conf.z, conf.w, conf.d, h,
        this.mats.vitrifiedTile, this.mats.wallInterior, parent, conf.label);
    }
  }

  // ─── GROUND FLOOR ───
  _createGroundFloor(floorData, parent) {
    const elev = floorData.elevation;
    const h = floorData.height;

    // Floor slab — polygon matching building footprint
    const floorPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
    const floor = MeshBuilder.CreatePolygon('gFloor', {
      shape: floorPath, depth: m(0.2),
    }, this.scene, earcut);
    floor.position.y = elev;
    floor.material = this.mats.designerTile;
    floor.parent = parent;
    floor.receiveShadows = true;

    // Build edge index for shared wall detection
    this._buildEdgeIndex([...GROUND_FLOOR.showrooms, ...GROUND_FLOOR.specialAreas]);

    // Showrooms
    GROUND_FLOOR.showrooms.forEach((unit) => {
      this._createUnit(unit, elev, h, parent, unit.type || 'showroom');
    });

    // Special areas (skip wall-less types like passages and voids)
    GROUND_FLOOR.specialAreas.forEach((area) => {
      const aType = (area.type || '').toLowerCase();
      if (aType === 'passage' || aType === 'void') return; // open areas, no walls
      const floorMat = area.id === 'RECEPTION' ? this.mats.marble : this.mats.designerTile;
      this._createRoom(
        area.id, area.x, elev, area.z, area.w, area.d,
        h, // Ground floor IS double height (14ft), no need to multiply
        floorMat, this.mats.wallInterior, parent, area.label
      );
    });

    // Furnish reception (if inside footprint)
    const rec = AMENITIES.reception;
    if (this._isInsideFootprint(rec.x + rec.w / 2, rec.z + rec.d / 2)) {
      this.furniture.furnishReception(rec.x, elev, rec.z, parent);
    }

    // Furnish lounge (if inside footprint)
    const lounge = AMENITIES.lounge;
    if (this._isInsideFootprint(lounge.x + lounge.w / 2, lounge.z + lounge.d / 2)) {
      this.furniture.furnishLounge(lounge.x, elev, lounge.z, lounge.w, lounge.d, parent);
    }

    // Furnish showrooms with display counters + ceiling lights
    GROUND_FLOOR.showrooms.forEach((unit) => {
      if (!this._isInsideFootprint(unit.x + unit.w / 2, unit.z + unit.d / 2)) return;
      this.furniture.furnishShowroom(unit.id + '_gf', unit.x, elev, unit.z, unit.w, unit.d, parent);
    });

    // Core
    this._createCore(elev, h, parent);
  }

  // ─── UPPER FLOORS ───
  _createUpperFloor(key, floorData, parent) {
    const elev = floorData.elevation;
    const h = floorData.height;
    const floorNum = parseInt(key);

    // Floor slab — polygon matching building footprint
    const floorPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
    const uFloor = MeshBuilder.CreatePolygon(`uFloor_${key}`, {
      shape: floorPath, depth: m(0.2),
    }, this.scene, earcut);
    uFloor.position.y = elev;
    uFloor.material = this.mats.vitrifiedTile;
    uFloor.parent = parent;

    // Build edge index for shared wall detection
    this._buildEdgeIndex([...UPPER_FLOOR.showrooms, ...UPPER_FLOOR.offices]);

    // Showrooms (east facing)
    UPPER_FLOOR.showrooms.forEach((unit) => {
      const unitCopy = { ...unit, id: `${unit.id.replace('1', floorNum)}` };
      this._createUnit(unitCopy, elev, h, parent, unit.type || 'showroom');
    });

    // South-facing showrooms
    UPPER_FLOOR.southShowrooms.forEach((unit) => {
      const unitCopy = { ...unit, id: `F${key}_${unit.id}` };
      this._createUnit(unitCopy, elev, h, parent, 'showroom');
    });

    // Offices
    UPPER_FLOOR.offices.forEach((unit) => {
      const unitCopy = { ...unit, id: `F${key}_${unit.id}` };
      this._createUnit(unitCopy, elev, h, parent, 'office');
    });

    // Passages — skip if center is outside footprint
    UPPER_FLOOR.passages.forEach((p) => {
      const pcx = p.x + p.w / 2, pcz = p.z + p.d / 2;
      if (!this._isInsideFootprint(pcx, pcz)) return;
      const passage = MeshBuilder.CreateBox(`passage_${key}_${p.id}`, {
        width: p.w, height: m(0.02), depth: p.d,
      }, this.scene);
      passage.position = new Vector3(pcx, elev + m(0.11), pcz);
      passage.material = this.mats.designerTile;
      passage.parent = parent;
    });

    // Furnish offices/showrooms only if inside footprint
    if (floorNum === 1) {
      UPPER_FLOOR.offices.forEach((unit) => {
        if (!this._isInsideFootprint(unit.x + unit.w / 2, unit.z + unit.d / 2)) return;
        this.furniture.furnishOffice(
          `F${key}_${unit.id}`, unit.x, elev, unit.z, unit.w, unit.d, parent
        );
      });

      UPPER_FLOOR.showrooms.forEach((unit) => {
        if (!this._isInsideFootprint(unit.x + unit.w / 2, unit.z + unit.d / 2)) return;
        this.furniture.furnishShowroom(
          `F${key}_${unit.id}_sr`, unit.x, elev, unit.z, unit.w, unit.d, parent
        );
      });
    }

    // Core
    this._createCore(elev, h, parent);
  }

  // ─── CORE (shared elevator/stairs) ───
  _createCore(elev, h, parent) {
    CORE.elements.forEach((elem) => {
      // Skip core elements outside the building footprint
      const cx = elem.x + elem.w / 2, cz = elem.z + elem.d / 2;
      if (!this._isInsideFootprint(cx, cz)) return;

      if (elem.type === 'elevator') {
        const shaft = MeshBuilder.CreateBox(`core_${elem.label}_${elev}`, {
          width: elem.w, height: h, depth: elem.d,
        }, this.scene);
        shaft.position = new Vector3(cx, elev + h / 2, cz);
        shaft.material = this.mats.aluminium;
        shaft.parent = parent;
      } else if (elem.type === 'stairs') {
        const stair = MeshBuilder.CreateBox(`core_stair_${elev}_${elem.x}`, {
          width: elem.w, height: h, depth: elem.d,
        }, this.scene);
        stair.position = new Vector3(cx, elev + h / 2, cz);
        stair.material = this.mats.concrete;
        stair.parent = parent;
      }
    });
  }

  // ─── SHARED WALL DETECTION ───

  _getEdges(unit) {
    if (unit.vertices && unit.vertices.length >= 3) {
      const edges = [];
      for (let i = 0; i < unit.vertices.length; i++) {
        const j = (i + 1) % unit.vertices.length;
        edges.push({
          x0: unit.vertices[i].x, z0: unit.vertices[i].z,
          x1: unit.vertices[j].x, z1: unit.vertices[j].z,
        });
      }
      return edges;
    }
    // Rectangle edges: N, S, W, E
    return [
      { x0: unit.x, z0: unit.z, x1: unit.x + unit.w, z1: unit.z },                 // N
      { x0: unit.x, z0: unit.z + unit.d, x1: unit.x + unit.w, z1: unit.z + unit.d }, // S
      { x0: unit.x, z0: unit.z, x1: unit.x, z1: unit.z + unit.d },                   // W
      { x0: unit.x + unit.w, z0: unit.z, x1: unit.x + unit.w, z1: unit.z + unit.d }, // E
    ];
  }

  _edgesOverlap(e1, e2) {
    // Check if two edges are collinear and overlapping
    const tol = 0.5;

    // Check axis-aligned first (most common)
    const e1Horiz = Math.abs(e1.z0 - e1.z1) < tol;
    const e2Horiz = Math.abs(e2.z0 - e2.z1) < tol;
    const e1Vert = Math.abs(e1.x0 - e1.x1) < tol;
    const e2Vert = Math.abs(e2.x0 - e2.x1) < tol;

    if (e1Horiz && e2Horiz && Math.abs(e1.z0 - e2.z0) < tol) {
      // Both horizontal at same z — check x overlap
      const min1 = Math.min(e1.x0, e1.x1), max1 = Math.max(e1.x0, e1.x1);
      const min2 = Math.min(e2.x0, e2.x1), max2 = Math.max(e2.x0, e2.x1);
      return max1 > min2 + tol && max2 > min1 + tol;
    }
    if (e1Vert && e2Vert && Math.abs(e1.x0 - e2.x0) < tol) {
      // Both vertical at same x — check z overlap
      const min1 = Math.min(e1.z0, e1.z1), max1 = Math.max(e1.z0, e1.z1);
      const min2 = Math.min(e2.z0, e2.z1), max2 = Math.max(e2.z0, e2.z1);
      return max1 > min2 + tol && max2 > min1 + tol;
    }

    return false;
  }

  _buildEdgeIndex(units) {
    // Pre-compute edges for all units for shared wall detection
    // Exclude passages/voids — they render no walls, so their edges should not
    // suppress walls of adjacent showrooms.
    this._edgeIndex = [];
    units.forEach((unit, i) => {
      const uType = (unit.type || '').toLowerCase();
      if (uType === 'passage' || uType === 'void') return;
      const edges = this._getEdges(unit);
      edges.forEach(edge => {
        this._edgeIndex.push({ unitIdx: i, unitId: unit.id, edge });
      });
    });
  }

  _isSharedWall(x0, z0, x1, z1, unitId) {
    if (!this._edgeIndex) return false;
    const testEdge = { x0, z0, x1, z1 };
    for (const entry of this._edgeIndex) {
      if (entry.unitId === unitId) continue;
      if (this._edgesOverlap(testEdge, entry.edge)) return true;
    }
    return false;
  }

  /** Check if a wall edge aligns with the building exterior facade (FOOTPRINT_OUTER).
   *  Walls on exterior edges should be skipped so the glass facade is visible.
   *  Uses midpoint-to-segment distance + parallel check for robust diagonal detection. */
  _isExteriorEdge(x0, z0, x1, z1) {
    const tol = 3.0; // feet — generous tolerance for polygon vertex approximation
    const mx = (x0 + x1) / 2, mz = (z0 + z1) / 2;
    const dx = x1 - x0, dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    if (len < 0.1) return false;
    const ux = dx / len, uz = dz / len;

    const n = FOOTPRINT_OUTER.length;
    for (let i = 0; i < n; i++) {
      const a = FOOTPRINT_OUTER[i];
      const b = FOOTPRINT_OUTER[(i + 1) % n];
      const fdx = b.x - a.x, fdz = b.z - a.z;
      const flen = Math.hypot(fdx, fdz);
      if (flen < 0.1) continue;
      const fux = fdx / flen, fuz = fdz / flen;

      // Check roughly parallel (dot product of unit directions close to ±1)
      const dot = Math.abs(ux * fux + uz * fuz);
      if (dot < 0.9) continue;

      // Check midpoint distance to footprint segment
      const dist = this._ptSegDist(mx, mz, a.x, a.z, b.x, b.z);
      if (dist < tol) return true;
    }
    return false;
  }

  /** Like _isExteriorEdge but ONLY matches diagonal footprint segments.
   *  Used by polygon showrooms to identify the glass facade edge without
   *  accidentally matching axis-aligned perimeter walls. */
  _isDiagonalFacadeEdge(x0, z0, x1, z1) {
    const tol = 3.0;
    const mx = (x0 + x1) / 2, mz = (z0 + z1) / 2;
    const dx = x1 - x0, dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    if (len < 0.1) return false;
    const ux = dx / len, uz = dz / len;

    const n = FOOTPRINT_OUTER.length;
    for (let i = 0; i < n; i++) {
      const a = FOOTPRINT_OUTER[i];
      const b = FOOTPRINT_OUTER[(i + 1) % n];
      const fdx = b.x - a.x, fdz = b.z - a.z;
      const flen = Math.hypot(fdx, fdz);
      if (flen < 0.1) continue;
      // Only match diagonal edges (both dx and dz significant)
      if (Math.abs(fdx) < 0.5 || Math.abs(fdz) < 0.5) continue;
      const fux = fdx / flen, fuz = fdz / flen;
      const dot = Math.abs(ux * fux + uz * fuz);
      if (dot < 0.9) continue;
      const dist = this._ptSegDist(mx, mz, a.x, a.z, b.x, b.z);
      if (dist < tol) return true;
    }
    return false;
  }

  /** Point-to-segment distance in 2D */
  _ptSegDist(px, pz, ax, az, bx, bz) {
    const dx = bx - ax, dz = bz - az;
    const len2 = dx * dx + dz * dz;
    if (len2 < 0.01) return Math.hypot(px - ax, pz - az);
    let t = ((px - ax) * dx + (pz - az) * dz) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
  }

  // ─── POLYGON UNIT (3D) ───
  _createPolygonUnit(unit, elev, h, parent, type) {
    const verts = unit.vertices;
    if (!verts || verts.length < 3) return;

    const wallThick = m(0.12);

    // Floor polygon
    const floorPath = verts.map(v => new Vector3(v.x, 0, v.z));
    const floorMesh = MeshBuilder.CreatePolygon(`${unit.id}_floor`, {
      shape: floorPath,
      depth: m(0.02),
    }, this.scene, earcut);
    floorMesh.position.y = (type === 'showroom' || type === 'Showroom') ? elev + 4.05 : elev + m(0.11);
    const lt = (type || '').toLowerCase();
    const isOctFloor = lt === 'showroom' || lt === 'office' || lt === 'shop';
    floorMesh.material = isOctFloor ? this.mats.octagonTile : this.mats.vitrifiedTile;
    floorMesh.parent = parent;
    if (isOctFloor) this._setWorldXZUVs(floorMesh);

    // Skip ceiling for showrooms — visible through glass facade as artifacts
    if (type !== 'showroom' && type !== 'Showroom') {
      const ceilMesh = MeshBuilder.CreatePolygon(`${unit.id}_ceil`, {
        shape: floorPath,
        depth: m(0.02),
      }, this.scene, earcut);
      ceilMesh.position.y = elev + h - m(0.01);
      ceilMesh.material = this.mats.ceiling;
      ceilMesh.parent = parent;
    }

    // Walls — one per edge
    const wallH = h - m(0.3);
    const nEdges = verts.length;
    const isShowroom = type === 'showroom' || type === 'Showroom';

    // For showrooms: ONLY skip the glass facade edge (diagonal front).
    // Render all other edges (back, left, right) — including shared boundaries.
    // Overlapping thin walls at shared boundaries is fine visually.
    const skipEdge = new Set();
    if (isShowroom) {
      for (let i = 0; i < nEdges; i++) {
        const j = (i + 1) % nEdges;
        if (this._isDiagonalFacadeEdge(verts[i].x, verts[i].z, verts[j].x, verts[j].z)) {
          skipEdge.add(i);
          break;
        }
      }
    }

    for (let i = 0; i < nEdges; i++) {
      const j = (i + 1) % nEdges;
      const v0 = verts[i], v1 = verts[j];

      if (skipEdge.has(i)) continue;

      // For non-showroom polygon units: use shared wall + exterior edge checks
      if (!isShowroom) {
        if (this._isSharedWall(v0.x, v0.z, v1.x, v1.z, unit.id)) continue;
        if (this._isExteriorEdge(v0.x, v0.z, v1.x, v1.z)) continue;
      }

      const edgeLen = Math.hypot(v1.x - v0.x, v1.z - v0.z);
      if (edgeLen < 0.1) continue;

      const cx = (v0.x + v1.x) / 2;
      const cz = (v0.z + v1.z) / 2;
      const angle = Math.atan2(-(v1.z - v0.z), v1.x - v0.x);

      const wallMesh = MeshBuilder.CreateBox(`${unit.id}_wall_${i}`, {
        width: edgeLen, height: wallH, depth: wallThick,
      }, this.scene);
      wallMesh.position = new Vector3(cx, elev + wallH / 2 + m(0.15), cz);
      wallMesh.rotation.y = angle;
      wallMesh.material = this.mats.wallInterior;
      wallMesh.parent = parent;
    }

    // Clickable area
    const clickArea = MeshBuilder.CreatePolygon(`${unit.id}_click`, {
      shape: floorPath,
      depth: m(0.05),
    }, this.scene, earcut);
    clickArea.position.y = elev + m(0.15);
    clickArea.material = this.mats.vitrifiedTile;
    clickArea.parent = parent;
    clickArea.isPickable = true;

    clickArea.metadata = {
      unitId: unit.id,
      label: unit.label,
      dim: unit.dim || '',
      type: type,
    };

    clickArea.actionManager = new ActionManager(this.scene);
    clickArea.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
        this._selectUnit(clickArea);
      })
    );

    this.unitMeshes.push(clickArea);
  }

  // ─── UNIT (Showroom or Office) ───
  _createUnit(unit, elev, h, parent, type) {
    // Skip units whose center is outside the building footprint
    const cx = unit.x + unit.w / 2, cz = unit.z + unit.d / 2;
    if (!this._isInsideFootprint(cx, cz)) return;

    // Branch: polygon vs rectangle
    if (unit.vertices && unit.vertices.length >= 3) {
      return this._createPolygonUnit(unit, elev, h, parent, type);
    }
    const wallThick = m(0.12);

    // Floor
    const floor = MeshBuilder.CreateBox(`${unit.id}_floor`, {
      width: unit.w, height: m(0.02), depth: unit.d,
    }, this.scene);
    floor.position = new Vector3(unit.x + unit.w / 2, elev + m(0.11), unit.z + unit.d / 2);
    floor.material = this.mats.vitrifiedTile;
    floor.parent = parent;

    // Skip ceiling for showrooms — visible through glass facade as artifacts
    const isShowroomType = type === 'showroom' || type === 'Showroom';
    if (!isShowroomType) {
      const ceil = MeshBuilder.CreateBox(`${unit.id}_ceil`, {
        width: unit.w, height: m(0.02), depth: unit.d,
      }, this.scene);
      ceil.position = new Vector3(unit.x + unit.w / 2, elev + h - m(0.01), unit.z + unit.d / 2);
      ceil.material = this.mats.ceiling;
      ceil.parent = parent;
    }

    // Walls (4 sides) — skip shared walls, exterior facade, and entrance for showrooms
    // Wall indices: 0=North, 1=South, 2=West, 3=East; opposite pairs: 0↔1, 2↔3
    const wallDefs = [
      // North wall
      { x: unit.x, z: unit.z, w: unit.w, d: wallThick, dir: 'EW',
        ex0: unit.x, ez0: unit.z, ex1: unit.x + unit.w, ez1: unit.z },
      // South wall
      { x: unit.x, z: unit.z + unit.d - wallThick, w: unit.w, d: wallThick, dir: 'EW',
        ex0: unit.x, ez0: unit.z + unit.d, ex1: unit.x + unit.w, ez1: unit.z + unit.d },
      // West wall
      { x: unit.x, z: unit.z, w: wallThick, d: unit.d, dir: 'NS',
        ex0: unit.x, ez0: unit.z, ex1: unit.x, ez1: unit.z + unit.d },
      // East wall
      { x: unit.x + unit.w - wallThick, z: unit.z, w: wallThick, d: unit.d, dir: 'NS',
        ex0: unit.x + unit.w, ez0: unit.z, ex1: unit.x + unit.w, ez1: unit.z + unit.d },
    ];

    // For showrooms: ONLY skip the glass facade wall. Render all other 3 sides.
    // Don't skip shared walls — overlapping thin walls at boundaries is fine visually.
    const skipWall = new Set();
    const isShowroom = type === 'showroom' || type === 'Showroom';
    if (isShowroom) {
      for (let wi = 0; wi < wallDefs.length; wi++) {
        const wall = wallDefs[wi];
        if (this._isExteriorEdge(wall.ex0, wall.ez0, wall.ex1, wall.ez1)) {
          skipWall.add(wi);
          break; // only one facade per showroom
        }
      }
    }

    wallDefs.forEach((wall, wi) => {
      if (skipWall.has(wi)) return;
      // For non-showroom units: use shared wall + exterior edge checks
      if (!isShowroom) {
        if (this._isSharedWall(wall.ex0, wall.ez0, wall.ex1, wall.ez1, unit.id)) return;
        if (this._isExteriorEdge(wall.ex0, wall.ez0, wall.ex1, wall.ez1)) return;
      }

      const wallMesh = MeshBuilder.CreateBox(`${unit.id}_wall_${wi}`, {
        width: wall.w, height: h - m(0.3), depth: wall.d,
      }, this.scene);
      wallMesh.position = new Vector3(
        wall.x + wall.w / 2,
        elev + (h - m(0.3)) / 2 + m(0.15),
        wall.z + wall.d / 2
      );
      wallMesh.material = this.mats.wallInterior;
      wallMesh.parent = parent;
    });

    // Clickable area (invisible, for unit selection)
    const clickArea = MeshBuilder.CreateBox(`${unit.id}_click`, {
      width: unit.w - m(0.3), height: m(0.05), depth: unit.d - m(0.3),
    }, this.scene);
    clickArea.position = new Vector3(unit.x + unit.w / 2, elev + m(0.15), unit.z + unit.d / 2);
    clickArea.material = this.mats.vitrifiedTile;
    clickArea.parent = parent;
    clickArea.isPickable = true;

    // Store metadata
    clickArea.metadata = {
      unitId: unit.id,
      label: unit.label,
      dim: unit.dim || '',
      type: type,
    };

    // Click action
    clickArea.actionManager = new ActionManager(this.scene);
    clickArea.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
        this._selectUnit(clickArea);
      })
    );

    this.unitMeshes.push(clickArea);
  }

  // ─── ROOM (special area — no click) ───
  _createRoom(id, x, elev, z, w, d, h, floorMat, wallMat, parent, label) {
    // Skip rooms whose center is outside the building footprint
    if (!this._isInsideFootprint(x + w / 2, z + d / 2)) return;
    const wallThick = m(0.12);

    const floor = MeshBuilder.CreateBox(`${id}_floor`, {
      width: w, height: m(0.02), depth: d,
    }, this.scene);
    floor.position = new Vector3(x + w / 2, elev + m(0.11), z + d / 2);
    floor.material = floorMat;
    floor.parent = parent;

    // Only 3 walls (leave one side open as entrance), skip walls on building exterior
    [
      { wx: x, wz: z, ww: w, wd: wallThick, ex0: x, ez0: z, ex1: x + w, ez1: z },
      { wx: x, wz: z, ww: wallThick, wd: d, ex0: x, ez0: z, ex1: x, ez1: z + d },
      { wx: x + w - wallThick, wz: z, ww: wallThick, wd: d, ex0: x + w, ez0: z, ex1: x + w, ez1: z + d },
    ].forEach((wall, wi) => {
      if (this._isExteriorEdge(wall.ex0, wall.ez0, wall.ex1, wall.ez1)) return;
      const wallMesh = MeshBuilder.CreateBox(`${id}_wall_${wi}`, {
        width: wall.ww, height: h - m(0.3), depth: wall.wd,
      }, this.scene);
      wallMesh.position = new Vector3(
        wall.wx + wall.ww / 2, elev + (h - m(0.3)) / 2 + m(0.15), wall.wz + wall.wd / 2
      );
      wallMesh.material = wallMat;
      wallMesh.parent = parent;
    });
  }

  // ─── DIAGONAL GEOMETRY HELPER ───
  _getDiagonalGeometry() {
    if (this._diagGeom) return this._diagGeom;
    const oS = STAIR_OUTLINE[3]; // outer-south (289, 264)
    const oN = STAIR_OUTLINE[2]; // outer-north (118.5, 367)
    const dx = oN.x - oS.x;     // -170.5
    const dz = oN.z - oS.z;     // 103
    const len = Math.hypot(dx, dz);
    this._diagGeom = {
      oS, oN, dx, dz, len,
      ex: dx / len, ez: dz / len,       // unit along-edge (south→north)
      nx: dz / len, nz: -dx / len,      // unit outward normal (toward road)
      rotY: Math.atan2(-dz, dx),         // Y rotation to align box width with diagonal
    };
    return this._diagGeom;
  }

  // ─── TREE HELPER (procedural instanced tree) ───
  _createTreeAt(x, baseY, z, name) {
    this._placeTreeInstance('small', x, baseY, z, name);
  }

  // ─── UV REMAP for parallelogram polygons ───
  // Sets UVs to world XZ coordinates (in feet) so all meshes sharing
  // the same material get consistent, seamlessly tiling textures.
  _setWorldXZUVs(mesh) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    const newUVs = [];
    for (let i = 0; i < positions.length; i += 3) {
      newUVs.push(positions[i], positions[i + 2]);
    }
    mesh.setVerticesData(VertexBuffer.UVKind, newUVs);
  }

  /** Triplanar world-space UVs — uniform scaling on horizontal treads AND vertical risers. */
  _setTriplanarWorldUVs(mesh) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    if (!positions || !normals) return;
    const wm = mesh.computeWorldMatrix(true);
    const newUVs = [];
    for (let i = 0; i < positions.length; i += 3) {
      const wp = Vector3.TransformCoordinates(
        new Vector3(positions[i], positions[i + 1], positions[i + 2]), wm
      );
      const nx = Math.abs(normals[i]), ny = Math.abs(normals[i + 1]), nz = Math.abs(normals[i + 2]);
      if (ny >= nx && ny >= nz) {
        // Horizontal face (top/bottom) — project XZ
        newUVs.push(wp.x, wp.z);
      } else if (nx >= nz) {
        // Facing X — project ZY
        newUVs.push(wp.z, wp.y);
      } else {
        // Facing Z — project XY
        newUVs.push(wp.x, wp.y);
      }
    }
    mesh.setVerticesData(VertexBuffer.UVKind, newUVs);
  }

  /** Remap UVs so V always = Y (vertical). Ensures vertical texture ribs stay vertical on all side faces. */
  _setVerticalRibUVs(mesh) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    const newUVs = [];
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i], y = positions[i + 1], z = positions[i + 2];
      const nx = normals[i], ny = normals[i + 1], nz = normals[i + 2];
      if (Math.abs(ny) > 0.5) {
        // Top/bottom face — use X,Z
        newUVs.push(x, z);
      } else {
        // Side face — U = horizontal extent along face, V = height
        const u = Math.abs(nx) > Math.abs(nz) ? z : x;
        newUVs.push(u, y);
      }
    }
    mesh.setVerticesData(VertexBuffer.UVKind, newUVs);
  }

  // Create open-top planter from 4 walls (no solid top face)
  _createPlanterWalls(name, cx, cy, cz, w, d, h, rotY) {
    const wt = 0.2; // wall thickness
    const walls = [];
    // Front/back walls (full width)
    walls.push(MeshBuilder.CreateBox(`${name}_f`, { width: w, height: h, depth: wt }, this.scene));
    walls[0].position.set(0, 0, d / 2 - wt / 2);
    walls.push(MeshBuilder.CreateBox(`${name}_b`, { width: w, height: h, depth: wt }, this.scene));
    walls[1].position.set(0, 0, -d / 2 + wt / 2);
    // Side walls (fit between front/back)
    walls.push(MeshBuilder.CreateBox(`${name}_l`, { width: wt, height: h, depth: d - 2 * wt }, this.scene));
    walls[2].position.set(-w / 2 + wt / 2, 0, 0);
    walls.push(MeshBuilder.CreateBox(`${name}_r`, { width: wt, height: h, depth: d - 2 * wt }, this.scene));
    walls[3].position.set(w / 2 - wt / 2, 0, 0);
    // Apply UVs before merge
    walls.forEach(wl => {
      wl.material = this.mats.curb;
      this._setVerticalRibUVs(wl);
    });
    const merged = Mesh.MergeMeshes(walls, true, true, undefined, false, true);
    merged.name = name;
    merged.position = new Vector3(cx, cy, cz);
    merged.rotation.y = rotY || 0;
    merged.material = this.mats.curb;
    return merged;
  }

  // ─── GROUND PAD (0.50ft elevated concrete between stairs and road) ───
  _createGroundPad() {
    if (!GROUND_PAD_OUTLINE || GROUND_PAD_OUTLINE.length < 3) return;
    const padShape = GROUND_PAD_OUTLINE.map(v => new Vector3(v.x, 0, v.z));
    const pad = MeshBuilder.CreatePolygon('groundPad', {
      shape: padShape, depth: 0.5,
    }, this.scene, earcut);
    pad.position.y = 0.5;
    pad.material = this.mats.groundPadPaver;
    this._setWorldXZUVs(pad);
  }

  // ─── SURFACE PARKING (data-driven from PARKING_GROUPS) ───
  _createSurfaceParking() {
    PARKING_GROUPS.forEach((grp, gi) => {
      const verts = grp.vertices;
      if (!verts || verts.length < 4) return;

      // Slab polygon
      const shape = verts.map(v => new Vector3(v.x, 0, v.z));
      const slab = MeshBuilder.CreatePolygon(`parkGrp_${gi}`, {
        shape, depth: 0.25,
      }, this.scene, earcut);
      slab.position.y = 0.76;
      slab.material = this.mats.parkingSurface;
      this._setWorldXZUVs(slab);

      // Parking lines — evenly distributed edge-to-edge
      // v0→v3 is the "width" edge, v0→v1 / v3→v2 are the "depth" edges
      const edgeDx = verts[3].x - verts[0].x;
      const edgeDz = verts[3].z - verts[0].z;
      const groupWidth = Math.hypot(edgeDx, edgeDz);

      const spotWidth = grp.spotWidth || 9;
      const numSpots = Math.max(1, Math.round(groupWidth / spotWidth));

      for (let si = 0; si <= numSpots; si++) {
        const t = si / numSpots;
        // Point on edge v0→v3
        const ax = verts[0].x + t * (verts[3].x - verts[0].x);
        const az = verts[0].z + t * (verts[3].z - verts[0].z);
        // Point on edge v1→v2
        const bx = verts[1].x + t * (verts[2].x - verts[1].x);
        const bz = verts[1].z + t * (verts[2].z - verts[1].z);

        const lineLen = Math.hypot(bx - ax, bz - az);
        const midX = (ax + bx) / 2;
        const midZ = (az + bz) / 2;
        const lineAngle = Math.atan2(-(bz - az), bx - ax);

        const line = MeshBuilder.CreateBox(`pLine_${gi}_${si}`, {
          width: lineLen, height: 0.04, depth: 0.15,
        }, this.scene);
        line.position = new Vector3(midX, slab.position.y + 0.03, midZ);
        line.rotation.y = lineAngle;
        line.material = this.mats.parkingLine;
      }

      // "PARKING" label centered in the group
      const cx = (verts[0].x + verts[1].x + verts[2].x + verts[3].x) / 4;
      const cz = (verts[0].z + verts[1].z + verts[2].z + verts[3].z) / 4;
      const labelAngle = Math.atan2(-(verts[3].z - verts[0].z), verts[3].x - verts[0].x);
      const labelW = Math.min(groupWidth * 0.85, 7);
      this._createGroundLabel(`parkLabel_${gi}`, 'PARKING', cx, slab.position.y + 0.04, cz, labelAngle, labelW);
    });
  }

  // ─── GROUND TEXT LABEL (DynamicTexture on a flat plane) ───
  _createGroundLabel(name, text, x, y, z, rotY, labelWidth, opts) {
    const { fontSize = 72, texW = 512, texH = 128, color = '#ffffff' } = opts || {};
    const tex = new DynamicTexture(`${name}_tex`, { width: texW, height: texH }, this.scene, true);
    const ctx = tex.getContext();
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, texW / 2, texH / 2);
    tex.update();
    tex.hasAlpha = true;
    tex.uScale = -1;    // flip horizontally for right-handed coordinate system
    tex.uOffset = 1;    // shift UV back into [0,1] range

    const mat = new PBRMaterial(`${name}_mat`, this.scene);
    mat.albedoTexture = tex;
    mat.opacityTexture = tex;
    mat.transparencyMode = 2;
    mat.metallic = 0;
    mat.roughness = 1;
    mat.backFaceCulling = false;
    mat.emissiveColor = new Color3(0.9, 0.9, 0.9);

    const labelH = labelWidth * (texH / texW);
    const ground = MeshBuilder.CreateGround(name, { width: labelWidth, height: labelH }, this.scene);
    ground.rotation.y = rotY;
    ground.position = new Vector3(x, y, z);
    ground.material = mat;
    return ground;
  }

  // ─── GRASS BLADE SYSTEM (procedural thin-instanced blades) ───

  /** Initialize grass accumulator — call before creating planters. */
  _initGrass() {
    this._grassMatrices = [];
    this._flowerMatrices = [];
    this._fountainGrassMatrices = [];
  }

  /**
   * Queue grass blades for a planter (accumulated, flushed later).
   * @param {number} cx,cy,cz — planter center
   * @param {number} w,d  — planter top width & depth (feet)
   * @param {number} rotY — planter Y rotation (radians)
   * @param {string} shape — 'rect' or 'round'
   */
  _addGrassToPlanter(id, cx, cy, cz, w, d, rotY, shape) {
    const rng = seededRandom(Math.floor(Math.abs(cx * 17 + cz * 53 + w * 7)));

    // Total slots: ~35 per sq ft — each slot is ONE type
    // 20% grass, 40% flower, 30% fountain grass, 10% empty
    const area = shape === 'round' ? Math.PI * (w / 2) * (d / 2) : w * d;
    const count = Math.min(Math.max(Math.floor(area * 35), 80), 5000);

    const halfW = Math.max(0.1, w / 2 - 0.05);
    const halfD = Math.max(0.1, d / 2 - 0.05);

    for (let attempt = 0, placed = 0; attempt < count * 2 && placed < count; attempt++) {
      const lx = (rng() - 0.5) * 2 * halfW;
      const lz = (rng() - 0.5) * 2 * halfD;

      if (shape === 'round') {
        const rx = lx / halfW, rz = lz / halfD;
        if (rx * rx + rz * rz > 1) continue;
      }

      const cosR = Math.cos(rotY), sinR = Math.sin(rotY);
      const wx = cx + lx * cosR + lz * sinR;
      const wz = cz - lx * sinR + lz * cosR;

      const edgeDist = Math.min(halfW - Math.abs(lx), halfD - Math.abs(lz));
      const yRotation = rng() * Math.PI * 2;

      // Decide what to place: 20% grass, 40% flower, 30% fountain grass, 10% empty
      const roll = rng();

      if (roll < 0.10) {
        // 10% — empty (open space)
        placed++;
        continue;
      }
      else if (roll < 0.30) {
        // 20% — normal grass (taller than before)
        const leanMax = edgeDist < 0.2 ? 0.15 : 0.5;
        const lean = (rng() - 0.5) * leanMax;
        const heightScale = 0.6 + rng() * 1.6; // 0.6–2.2 (taller)
        const sxMax = edgeDist < 0.2 ? 0.9 : 1.5;
        const sx = 0.5 + rng() * (sxMax - 0.5);
        const mat = Matrix.Identity();
        Matrix.ScalingToRef(sx, heightScale, sx, mat);
        const rotMat = Matrix.RotationYawPitchRoll(yRotation, lean, 0);
        mat.multiplyToRef(rotMat, mat);
        mat.setTranslation(new Vector3(wx, cy, wz));
        this._grassMatrices.push(mat);
      }
      else if (roll < 0.70 && edgeDist > 0.4) {
        // 40% — dandelion flower (taller than fountain grass)
        const fMat = Matrix.Identity();
        const fScale = 0.7 + rng() * 0.5; // 0.7–1.2 width
        const fHeight = 0.7 + rng() * 0.5; // 0.7–1.2 height
        Matrix.ScalingToRef(fScale, fHeight, fScale, fMat);
        const fRotMat = Matrix.RotationYawPitchRoll(yRotation, 0, 0);
        fMat.multiplyToRef(fRotMat, fMat);
        fMat.setTranslation(new Vector3(wx, cy, wz));
        this._flowerMatrices.push(fMat);
      }
      else if (roll < 1.0 && edgeDist > 0.5) {
        // 30% — fountain grass (shorter, natural bend)
        const fgMat = Matrix.Identity();
        const fgScale = 0.7 + rng() * 0.5; // 0.7–1.2 width
        const fgHeight = 0.5 + rng() * 0.4; // 0.5–0.9 height (shorter than flowers)
        Matrix.ScalingToRef(fgScale, fgHeight, fgScale, fgMat);
        // Natural physics bend — lean outward with gravity droop
        const fgLean = (rng() - 0.5) * 0.7; // ±0.35 rad strong random bend
        const fgRotMat = Matrix.RotationYawPitchRoll(yRotation, fgLean, (rng() - 0.5) * 0.15);
        fgMat.multiplyToRef(fgRotMat, fgMat);
        fgMat.setTranslation(new Vector3(wx, cy, wz));
        this._fountainGrassMatrices.push(fgMat);
      }
      else {
        // Fallback to grass if too close to edge for flower/fountain
        const lean = (rng() - 0.5) * 0.3;
        const heightScale = 0.6 + rng() * 1.4;
        const mat = Matrix.Identity();
        Matrix.ScalingToRef(0.7, heightScale, 0.7, mat);
        const rotMat = Matrix.RotationYawPitchRoll(yRotation, lean, 0);
        mat.multiplyToRef(rotMat, mat);
        mat.setTranslation(new Vector3(wx, cy, wz));
        this._grassMatrices.push(mat);
      }
      placed++;
    }
  }

  /** Flush all accumulated grass into a single thin-instanced mesh. */
  _flushGrass() {
    const total = this._grassMatrices.length;
    if (total === 0) return;

    // Quad geometry — texture alpha provides organic blade shapes
    const vd = new VertexData();
    const hw = 0.25; // half-width 0.25ft (total 0.5ft)
    const h = 0.45;  // height 0.45ft (short ground grass)
    vd.positions = [
      -hw, 0, 0,   // 0: bottom-left
       hw, 0, 0,   // 1: bottom-right
       hw, h, 0,   // 2: top-right
      -hw, h, 0,   // 3: top-left
    ];
    vd.indices = [0, 1, 2, 0, 2, 3];
    vd.normals = [];
    VertexData.ComputeNormals(vd.positions, vd.indices, vd.normals);
    vd.uvs = [0, 0, 1, 0, 1, 1, 0, 1];

    const grassMesh = new Mesh('grass_all', this.scene);
    vd.applyToMesh(grassMesh);
    grassMesh.material = this.mats.grassBlade;
    grassMesh.isPickable = false;

    const matBuf = new Float32Array(total * 16);
    for (let i = 0; i < total; i++) {
      this._grassMatrices[i].copyToArray(matBuf, i * 16);
    }
    grassMesh.thinInstanceSetBuffer('matrix', matBuf, 16);
    grassMesh.thinInstanceRefreshBoundingInfo(false);
    grassMesh.freezeWorldMatrix();

    // Flush flower instances (larger quads for visibility at distance)
    const flowerTotal = this._flowerMatrices.length;
    if (flowerTotal > 0) {
      const fvd = new VertexData();
      const fhw = 0.6, fh = 1.1; // flowers taller than fountain grass
      fvd.positions = [-fhw, 0, 0, fhw, 0, 0, fhw, fh, 0, -fhw, fh, 0];
      fvd.indices = [0, 1, 2, 0, 2, 3];
      fvd.normals = [];
      VertexData.ComputeNormals(fvd.positions, fvd.indices, fvd.normals);
      fvd.uvs = [0, 0, 1, 0, 1, 1, 0, 1];

      const flowerMesh = new Mesh('flowers_all', this.scene);
      fvd.applyToMesh(flowerMesh);
      flowerMesh.material = this.mats.flowerBlade;
      flowerMesh.isPickable = false;

      const fBuf = new Float32Array(flowerTotal * 16);
      for (let i = 0; i < flowerTotal; i++) {
        this._flowerMatrices[i].copyToArray(fBuf, i * 16);
      }
      flowerMesh.thinInstanceSetBuffer('matrix', fBuf, 16);
      flowerMesh.thinInstanceRefreshBoundingInfo(false);
      flowerMesh.freezeWorldMatrix();
    }

    // Flush fountain grass instances (tall plumes)
    const fgTotal = this._fountainGrassMatrices.length;
    if (fgTotal > 0) {
      const fgvd = new VertexData();
      const fghw = 0.7, fgh = 1.6; // fountain grass — tall plumes
      fgvd.positions = [-fghw, 0, 0, fghw, 0, 0, fghw, fgh, 0, -fghw, fgh, 0];
      fgvd.indices = [0, 1, 2, 0, 2, 3];
      fgvd.normals = [];
      VertexData.ComputeNormals(fgvd.positions, fgvd.indices, fgvd.normals);
      fgvd.uvs = [0, 0, 1, 0, 1, 1, 0, 1];

      const fgMesh = new Mesh('fountainGrass_all', this.scene);
      fgvd.applyToMesh(fgMesh);
      fgMesh.material = this.mats.fountainGrass;
      fgMesh.isPickable = false;

      const fgBuf = new Float32Array(fgTotal * 16);
      for (let i = 0; i < fgTotal; i++) {
        this._fountainGrassMatrices[i].copyToArray(fgBuf, i * 16);
      }
      fgMesh.thinInstanceSetBuffer('matrix', fgBuf, 16);
      fgMesh.thinInstanceRefreshBoundingInfo(false);
      fgMesh.freezeWorldMatrix();
    }

    this._grassMatrices = null;
    this._flowerMatrices = null;
    this._fountainGrassMatrices = null;
  }

  // ─── PLACED PLANTERS (data-driven from PLACED_PLANTERS) ───
  _createPlacedPlanters() {
    PLACED_PLANTERS.forEach((pl, i) => {
      const h = pl.height || 4.5;

      if ((pl.shape || 'round') === 'rect') {
        const w = pl.width || pl.diameter || 5;
        const dp = pl.depth || pl.diameter || 5;
        const rotY = (pl.angle || 0) * Math.PI / 180;
        this._createPlanterWalls(`planter_${i}`, pl.x, h / 2 + 0.5, pl.z, w, dp, h, rotY);

        const green = MeshBuilder.CreateBox(`planterGr_${i}`, { width: w - 0.4, height: 0.15, depth: dp - 0.4 }, this.scene);
        green.position = new Vector3(pl.x, h + 0.35, pl.z);
        green.rotation.y = rotY;
        green.material = this.mats.soil;
        this._addGrassToPlanter(`pl_${i}`, pl.x, h + 0.45, pl.z, w - 0.4, dp - 0.4, rotY, 'rect');
      } else {
        const d = pl.diameter || 5;
        const curb = MeshBuilder.CreateCylinder(`planter_${i}`, {
          diameter: d, height: h, tessellation: 24,
        }, this.scene);
        curb.position = new Vector3(pl.x, h / 2 + 0.5, pl.z);
        curb.material = this.mats.curb;
        this._setVerticalRibUVs(curb);

        const green = MeshBuilder.CreateCylinder(`planterGr_${i}`, {
          diameter: d - 0.4, height: 0.15, tessellation: 24,
        }, this.scene);
        green.position = new Vector3(pl.x, h + 0.35, pl.z);
        green.material = this.mats.soil;
        this._addGrassToPlanter(`pl_${i}`, pl.x, h + 0.45, pl.z, d - 0.4, d - 0.4, 0, 'round');
      }

      if (pl.hasTree) {
        this._createTreeAt(pl.x, h + 0.35, pl.z, `planterTr_${i}`);
      }
    });
  }

  // ─── PLACED ROADS (centerline-based, polygon surface + median planters) ───

  _segmentNormal3D(segments, idx) {
    let dx, dz;
    if (segments.length < 2) return { nx: 0, nz: 1 };
    if (idx === 0) {
      dx = segments[1].x - segments[0].x;
      dz = segments[1].z - segments[0].z;
    } else if (idx >= segments.length - 1) {
      dx = segments[segments.length - 1].x - segments[segments.length - 2].x;
      dz = segments[segments.length - 1].z - segments[segments.length - 2].z;
    } else {
      dx = segments[idx + 1].x - segments[idx - 1].x;
      dz = segments[idx + 1].z - segments[idx - 1].z;
    }
    const len = Math.hypot(dx, dz) || 1;
    return { nx: -dz / len, nz: dx / len };
  }

  _catmullRomTessellate3D(points, segsPerSpan) {
    segsPerSpan = segsPerSpan || 16;
    if (points.length < 2) return [...points];
    if (points.length === 2) return [...points];
    const result = [];
    const n = points.length;
    for (let i = 0; i < n - 1; i++) {
      const p0 = points[Math.max(i - 1, 0)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(i + 2, n - 1)];
      for (let s = 0; s < segsPerSpan; s++) {
        const t = s / segsPerSpan;
        const t2 = t * t, t3 = t2 * t;
        const x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
        const z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3);
        result.push({ x, z });
      }
    }
    result.push(points[n - 1]);
    return result;
  }

  _polylineLength3D(segments) {
    let len = 0;
    for (let i = 1; i < segments.length; i++) {
      len += Math.hypot(segments[i].x - segments[i - 1].x, segments[i].z - segments[i - 1].z);
    }
    return len;
  }

  _pointAtDistance3D(segments, dist) {
    let acc = 0;
    for (let i = 1; i < segments.length; i++) {
      const dx = segments[i].x - segments[i - 1].x;
      const dz = segments[i].z - segments[i - 1].z;
      const segLen = Math.hypot(dx, dz);
      if (acc + segLen >= dist) {
        const t = (dist - acc) / segLen;
        return {
          x: segments[i - 1].x + dx * t,
          z: segments[i - 1].z + dz * t,
          angle: Math.atan2(dx, dz),
        };
      }
      acc += segLen;
    }
    const last = segments[segments.length - 1];
    const prev = segments[segments.length - 2] || last;
    return { x: last.x, z: last.z, angle: Math.atan2(last.x - prev.x, last.z - prev.z) };
  }

  _createPlacedRoads() {
    PLACED_ROADS.forEach((rd, i) => {
      const pts = rd.points;
      if (!pts || pts.length < 2) return;
      const segments = rd.curved ? this._catmullRomTessellate3D(pts) : pts;
      const hw = (rd.width || 30) / 2;

      // Build road surface polygon
      const leftEdge = [], rightEdge = [];
      for (let j = 0; j < segments.length; j++) {
        const { nx, nz } = this._segmentNormal3D(segments, j);
        leftEdge.push(new Vector3(segments[j].x + nx * hw, 0, segments[j].z + nz * hw));
        rightEdge.push(new Vector3(segments[j].x - nx * hw, 0, segments[j].z - nz * hw));
      }

      // Create polygon from left edge + reversed right edge
      const shape = [...leftEdge, ...[...rightEdge].reverse()];
      const road = MeshBuilder.CreatePolygon(`placedRoad_${i}`, { shape, depth: 0.1 }, this.scene, earcut);
      road.position.y = 0.05;
      road.material = this.mats.road;
      road.receiveShadows = true;
      this._setWorldXZUVs(road);

      // Footpath (raised sidewalk on each edge)
      const fpW = rd.footpathWidth || 0;
      if (fpW > 0) {
        const fpLeftOuter = [], fpLeftInner = [];
        const fpRightOuter = [], fpRightInner = [];
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal3D(segments, j);
          fpLeftOuter.push(new Vector3(segments[j].x + nx * hw, 0, segments[j].z + nz * hw));
          fpLeftInner.push(new Vector3(segments[j].x + nx * (hw - fpW), 0, segments[j].z + nz * (hw - fpW)));
          fpRightOuter.push(new Vector3(segments[j].x - nx * hw, 0, segments[j].z - nz * hw));
          fpRightInner.push(new Vector3(segments[j].x - nx * (hw - fpW), 0, segments[j].z - nz * (hw - fpW)));
        }
        const fpLShape = [...fpLeftOuter, ...[...fpLeftInner].reverse()];
        const fpL = MeshBuilder.CreatePolygon(`roadFpL_${i}`, { shape: fpLShape, depth: 0.5 }, this.scene, earcut);
        fpL.position.y = 0.55;    // slightly above ground pad (0.5) to prevent z-fighting
        fpL.material = this.mats.sidewalk;
        this._setWorldXZUVs(fpL);
        const fpRShape = [...fpRightOuter, ...[...fpRightInner].reverse()];
        const fpR = MeshBuilder.CreatePolygon(`roadFpR_${i}`, { shape: fpRShape, depth: 0.5 }, this.scene, earcut);
        fpR.position.y = 0.55;    // slightly above ground pad (0.5) to prevent z-fighting
        fpR.material = this.mats.sidewalk;
        this._setWorldXZUVs(fpR);
      }

      // Yellow edge stripes
      const stripeW = rd.edgeStripeWidth || 0;
      if (stripeW > 0) {
        const stripeOff = hw - fpW;
        const stLOut = [], stLIn = [], stROut = [], stRIn = [];
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal3D(segments, j);
          stLOut.push(new Vector3(segments[j].x + nx * stripeOff, 0, segments[j].z + nz * stripeOff));
          stLIn.push(new Vector3(segments[j].x + nx * (stripeOff - stripeW), 0, segments[j].z + nz * (stripeOff - stripeW)));
          stROut.push(new Vector3(segments[j].x - nx * stripeOff, 0, segments[j].z - nz * stripeOff));
          stRIn.push(new Vector3(segments[j].x - nx * (stripeOff - stripeW), 0, segments[j].z - nz * (stripeOff - stripeW)));
        }
        const slShape = [...stLOut, ...[...stLIn].reverse()];
        const sl = MeshBuilder.CreatePolygon(`roadStrpL_${i}`, { shape: slShape, depth: 0.04 }, this.scene, earcut);
        sl.position.y = 0.14;
        sl.material = this.mats.parkingLine;
        const srShape = [...stROut, ...[...stRIn].reverse()];
        const sr = MeshBuilder.CreatePolygon(`roadStrpR_${i}`, { shape: srShape, depth: 0.04 }, this.scene, earcut);
        sr.position.y = 0.14;
        sr.material = this.mats.parkingLine;
      }

      // Road center dashed line markings
      for (let j = 0; j < segments.length - 1; j++) {
        const p1 = segments[j], p2 = segments[j + 1];
        const segLen = Math.hypot(p2.x - p1.x, p2.z - p1.z);
        const dx = (p2.x - p1.x) / segLen, dz = (p2.z - p1.z) / segLen;
        for (let d = 0; d < segLen; d += 6) {
          const dashLen = Math.min(3, segLen - d);
          const cx = p1.x + dx * (d + dashLen / 2);
          const cz = p1.z + dz * (d + dashLen / 2);
          const line = MeshBuilder.CreateBox(`placedRoadLine_${i}_${j}_${d | 0}`, {
            width: dashLen, height: 0.02, depth: 0.15,
          }, this.scene);
          line.position = new Vector3(cx, 0.12, cz);
          line.rotation.y = Math.atan2(-(p2.z - p1.z), p2.x - p1.x);
          line.material = this.mats.parkingLine;
        }
      }

      // Create median planters
      if (rd.medianPlanters) {
        this._createRoadMedianPlanters(rd, segments, i);
      }

      // Road labels — one between every pair of median planters
      this._createRoadLabels(rd, segments, i);
    });
  }

  _createRoadMedianPlanters(rd, segments, roadIdx) {
    const totalLen = this._polylineLength3D(segments);
    const roadW = rd.width || 200;
    const w = rd.medianPlanterWidthPct
      ? (rd.medianPlanterWidthPct / 100) * roadW
      : (rd.medianPlanterWidth || 30);
    const gap = rd.medianPlanterGapPct
      ? (rd.medianPlanterGapPct / 100) * roadW
      : ((rd.medianPlanterSpacing || 40) - (rd.medianPlanterWidth || 30));
    const spacing = w + gap;
    let dist = spacing / 2;
    let planterIdx = 0;
    while (dist < totalLen - spacing / 4) {
      const pt = this._pointAtDistance3D(segments, dist);
      const d = rd.medianPlanterDepth || 8;
      const h = rd.medianPlanterHeight || 3;

      const rotY = pt.angle + (rd.medianPlanterAngle || 0) * Math.PI / 180;

      this._createPlanterWalls(`roadMedPl_${roadIdx}_${planterIdx}`, pt.x, h / 2 + 0.1, pt.z, w, d, h, rotY);

      const green = MeshBuilder.CreateBox(`roadMedGr_${roadIdx}_${planterIdx}`, { width: w - 0.4, height: 0.15, depth: d - 0.4 }, this.scene);
      green.position = new Vector3(pt.x, h - 0.05, pt.z);
      green.rotation.y = rotY;
      green.material = this.mats.soil;
      this._addGrassToPlanter(`rM_${roadIdx}_${planterIdx}`, pt.x, h + 0.05, pt.z, w - 0.4, d - 0.4, rotY, 'rect');

      if (rd.medianHasTrees) {
        this._createTreeAt(pt.x, h - 0.05, pt.z, `roadMedTr_${roadIdx}_${planterIdx}`);
      }

      dist += spacing;
      planterIdx++;
    }
  }

  _createRoadLabels(rd, segments, roadIdx) {
    const totalLen = this._polylineLength3D(segments);
    const roadW = rd.width || 200;
    const w = rd.medianPlanterWidthPct
      ? (rd.medianPlanterWidthPct / 100) * roadW
      : (rd.medianPlanterWidth || 30);
    const gap = rd.medianPlanterGapPct
      ? (rd.medianPlanterGapPct / 100) * roadW
      : ((rd.medianPlanterSpacing || 40) - (rd.medianPlanterWidth || 30));
    const spacing = w + gap;
    const labelText = rd.label || '200 FT. WIDE ROAD';
    const rdAngle = Math.atan2(-(segments[1].z - segments[0].z), segments[1].x - segments[0].x);

    // Place a label centered in each gap between planters
    let dist = spacing / 2;
    let li = 0;
    while (dist < totalLen - spacing / 4) {
      // Gap center is at dist + w/2 + gap/2 = dist + (w + gap)/2 = dist + spacing/2
      // But first planter is at dist, next at dist+spacing, so gap center = dist + w/2 + gap/2
      const gapCenter = dist + w / 2 + gap / 2;
      if (gapCenter < totalLen) {
        const pt = this._pointAtDistance3D(segments, gapCenter);
        const labelW = Math.min(gap * 0.85, 45);
        this._createGroundLabel(`roadLabel_${roadIdx}_${li}`, labelText,
          pt.x, 0.14, pt.z, rdAngle, labelW,
          { texW: 1024, texH: 128, fontSize: 80 });
        li++;
      }
      dist += spacing;
    }
  }


  // ─── WALKWAY PLANTERS (large planters with trees on 4ft elevated walkway) ───
  _createWalkwayPlanters() {
    const wk = WALKWAY_OUTLINE;
    if (wk.length < 4) return;

    // WALKWAY_OUTLINE: [0]=inner-south(V6), [1]=inner-north(V7), [2]=outer-north, [3]=outer-south
    const iS = wk[0], iN = wk[1], oN = wk[2], oS = wk[3];
    const platformH = 4;

    // Diagonal direction for rotation
    const dx = iN.x - iS.x, dz = iN.z - iS.z;
    const diagRotY = Math.atan2(-dz, dx);

    WALKWAY_PLANTERS.forEach((pl, i) => {
      const t = pl.along;

      // Position on walkway — biased toward outer edge (65% from inner to outer)
      const ix = iS.x + t * (iN.x - iS.x);
      const iz = iS.z + t * (iN.z - iS.z);
      const ox = oS.x + t * (oN.x - oS.x);
      const oz = oS.z + t * (oN.z - oS.z);
      const cx = ix + 0.65 * (ox - ix);
      const cz = iz + 0.65 * (oz - iz);

      // Concrete planter walls (open top)
      this._createPlanterWalls(`wkPlanter_${i}`, cx, platformH + pl.height / 2, cz, pl.width, pl.depth, pl.height, diagRotY);

      // Soil inside planter
      const greenTop = MeshBuilder.CreateBox(`wkPlanterGreen_${i}`, {
        width: pl.width - 0.4, height: 0.15, depth: pl.depth - 0.4,
      }, this.scene);
      greenTop.position = new Vector3(cx, platformH + pl.height - 0.15, cz);
      greenTop.rotation.y = diagRotY;
      greenTop.material = this.mats.soil;
      this._addGrassToPlanter(`wk_${i}`, cx, platformH + pl.height - 0.05, cz, pl.width - 0.4, pl.depth - 0.4, diagRotY, 'rect');

      // Tree in planter
      this._createTreeAt(cx, platformH + pl.height - 0.15, cz, `wkTree_${i}`);
    });
  }

  // ─── GROUND PLANTERS (between trees along sidewalk) ───
  _createPlanters() {
    const facadeSegs = this._buildFacadeSegments();

    // Planters along the east-facing edge (east sidewalk)
    const eastSeg = facadeSegs.find(s => s.dir === 'NS' && s.outwardSign > 0);
    if (eastSeg) {
      for (let z = eastSeg.start + m(2); z < eastSeg.start + eastSeg.len - m(4); z += m(10)) {
        this._createPlanterWalls(`gPlanter_E_${z}`, eastSeg.pos + m(1.5), m(0.25), z + m(1.5), m(1.2), m(3), m(0.5), 0);
        const greenTop = MeshBuilder.CreateBox(`gPlanterGreen_E_${z}`, {
          width: m(1.0), height: m(0.15), depth: m(2.8),
        }, this.scene);
        greenTop.position = new Vector3(eastSeg.pos + m(1.5), m(0.35), z + m(1.5));
        greenTop.material = this.mats.soil;
        this._addGrassToPlanter(`gE_${z}`, eastSeg.pos + m(1.5), m(0.45), z + m(1.5), m(1.2) - 0.4, m(3) - 0.4, 0, 'rect');
      }
    }

    // Planters along EW edges (north-facing steps)
    facadeSegs.filter(s => s.dir === 'EW').forEach((seg, si) => {
      const s = seg.outwardSign;
      for (let x = seg.start + m(2); x < seg.start + seg.len - m(4); x += m(8)) {
        this._createPlanterWalls(`gPlanter_${si}_${x}`, x + m(1.5), m(0.25), seg.pos + s * m(1.5), m(3), m(1.2), m(0.5), 0);
        const greenTop = MeshBuilder.CreateBox(`gPlanterGreen_${si}_${x}`, {
          width: m(2.8), height: m(0.15), depth: m(1.0),
        }, this.scene);
        greenTop.position = new Vector3(x + m(1.5), m(0.35), seg.pos + s * m(1.5));
        greenTop.material = this.mats.soil;
        this._addGrassToPlanter(`gS_${si}_${x}`, x + m(1.5), m(0.45), seg.pos + s * m(1.5), m(3) - 0.4, m(1.2) - 0.4, 0, 'rect');
      }
    });
  }

  // ─── COCONUT PALM GLB SYSTEM ───

  /** Load coconut_palm.glb and prepare master meshes for instancing. */
  async _loadPalmModel() {
    const result = await SceneLoader.ImportMeshAsync(
      '', '/coconut_tree.glb', '', this.scene
    );

    // Find the root and collect meshes with geometry
    const root = new TransformNode('palmMaster', this.scene);
    const masterMeshes = [];

    for (const mesh of result.meshes) {
      if (mesh.getTotalVertices() > 0) {
        mesh.parent = root;
        mesh.isVisible = false; // hide master, we'll instance it
        masterMeshes.push(mesh);
      }
    }

    // Measure bounding box to determine scale factor
    root.computeWorldMatrix(true);
    let minY = Infinity, maxY = -Infinity;
    for (const mesh of masterMeshes) {
      mesh.computeWorldMatrix(true);
      const bounds = mesh.getBoundingInfo().boundingBox;
      minY = Math.min(minY, bounds.minimumWorld.y);
      maxY = Math.max(maxY, bounds.maximumWorld.y);
    }
    const modelHeight = maxY - minY;
    // Optimize GLB materials for performance:
    // Switch from ALPHABLEND to ALPHATEST, enable back-face culling
    for (const mesh of masterMeshes) {
      const mat = mesh.material;
      if (!mat) continue;
      const n = mat.name.toLowerCase();
      if (n.includes('leav')) {
        mat.transparencyMode = 1; // ALPHATEST
        mat.alphaCutOff = 0.4;
        mat.backFaceCulling = true;
        mat.forceDepthWrite = true;
      } else if (n.includes('stem') || n.includes('trunk') || n.includes('bark')) {
        mat.transparencyMode = 0; // OPAQUE — trunk is solid, no alpha needed
        mat.backFaceCulling = true;
        mat.forceDepthWrite = true;
        if (mat.albedoTexture) mat.albedoTexture.hasAlpha = false;
      }
    }

    this._palmMaster = { root, meshes: masterMeshes, modelHeight };
  }

  /** Synchronous init — just stores a flag; actual loading is async. */
  _initMasterTrees() {
    this._palmMaster = null;
    this._pendingTreePlacements = [];
  }

  /** Place a palm at (x, baseY, z) — creates instances from the loaded GLB master. */
  _placeTreeInstance(type, x, baseY, z, name) {
    if (!this._palmMaster) {
      // Model not loaded yet, queue the placement
      this._pendingTreePlacements.push({ type, x, baseY, z, name });
      return;
    }

    const master = this._palmMaster;

    // Target heights: large ~30ft (9m), small ~18ft (5.5m)
    const targetHeight = type === 'large' ? m(9) : m(5.5);
    const baseScale = targetHeight / master.modelHeight;

    const node = new TransformNode(name, this.scene);
    node.position = new Vector3(x, baseY, z);

    // Deterministic Y rotation and scale variation
    const rng = seededRandom(Math.floor(Math.abs(x * 31 + z * 97)));
    node.rotation.y = rng() * Math.PI * 2;
    const scaleVar = baseScale * (0.85 + rng() * 0.30);
    node.scaling = new Vector3(scaleVar, scaleVar, scaleVar);

    // Instance each sub-mesh from master
    for (const mesh of master.meshes) {
      const inst = mesh.createInstance(`${name}_${mesh.name}`);
      inst.parent = node;
    }
  }

  /** Flush any tree placements that were queued before the GLB loaded. */
  _flushPendingTrees() {
    if (!this._pendingTreePlacements) return;
    for (const p of this._pendingTreePlacements) {
      this._placeTreeInstance(p.type, p.x, p.baseY, p.z, p.name);
    }
    this._pendingTreePlacements = [];
  }

  // ─── TREES ───
  _createTrees() {
    EXTERIOR.trees.forEach((tree, i) => {
      this._placeTreeInstance('large', tree.x, 0, tree.z, `tree_${i}`);

      // Tree base circle (dirt/mulch)
      const baseRing = MeshBuilder.CreateDisc(`treeBase_${i}`, {
        radius: m(0.8), tessellation: 16,
      }, this.scene);
      baseRing.position = new Vector3(tree.x, m(0.02), tree.z);
      baseRing.rotation.x = Math.PI / 2;
      baseRing.material = this.mats.bark;
    });
  }

  // ─── TERRACE GARDEN (35000 sqft) ───
  _createTerraceGarden() {
    const tg = EXTERIOR.terraceGarden;
    const topY = BUILDING.totalHeight + m(0.3);

    // Main terrace deck — polygon matching building footprint (clips to diagonal edge)
    const gardenPath = FOOTPRINT_OUTER.map(v => new Vector3(v.x, 0, v.z));
    const garden = MeshBuilder.CreatePolygon('terraceGarden', {
      shape: gardenPath, depth: m(0.3),
    }, this.scene, earcut);
    garden.position.y = topY;
    garden.material = this.mats.terrace;

    // Walking track (path loop around the perimeter)
    const trackW = m(1.5);
    const trackParts = [
      { x: tg.x + m(3), z: tg.z + m(3), w: tg.w - m(6), d: trackW },                     // South path
      { x: tg.x + m(3), z: tg.z + tg.d - m(3) - trackW, w: tg.w - m(6), d: trackW },     // North path
      { x: tg.x + m(3), z: tg.z + m(3), w: trackW, d: tg.d - m(6) },                      // West path
      { x: tg.x + tg.w - m(3) - trackW, z: tg.z + m(3), w: trackW, d: tg.d - m(6) },     // East path
    ];
    trackParts.forEach((tp, ti) => {
      const cx = tp.x + tp.w / 2, cz = tp.z + tp.d / 2;
      // Check all 4 corners — skip if any corner is outside the building footprint
      const corners = [
        [tp.x, tp.z], [tp.x + tp.w, tp.z],
        [tp.x, tp.z + tp.d], [tp.x + tp.w, tp.z + tp.d],
      ];
      if (!corners.every(([cx2, cz2]) => this._isInsideFootprint(cx2, cz2))) return;
      const track = MeshBuilder.CreateBox(`walkTrack_${ti}`, {
        width: tp.w, height: m(0.05), depth: tp.d,
      }, this.scene);
      track.position = new Vector3(cx, topY + m(0.32), cz);
      track.material = this.mats.sidewalk;
    });

    // Planters with mini trees (scattered in garden area — wider spacing for perf)
    for (let x = tg.x + m(8); x < tg.x + tg.w - m(8); x += m(20)) {
      for (let z = tg.z + m(8); z < tg.z + tg.d - m(8); z += m(20)) {
        // Skip planters outside the building footprint
        if (!this._isInsideFootprint(x, z)) continue;

        const planter = MeshBuilder.CreateBox(`planter_${x}_${z}`, {
          width: m(2.5), height: m(0.5), depth: m(2.5),
        }, this.scene);
        planter.position = new Vector3(x, topY + m(0.55), z);
        planter.material = this.mats.curb;

        // Mini tree/shrub
        const miniTree = MeshBuilder.CreateSphere(`miniTree_${x}_${z}`, {
          diameter: m(2.2), segments: 6,
        }, this.scene);
        miniTree.position = new Vector3(x, topY + m(2.0), z);
        miniTree.material = this.mats.leaves;
      }
    }

    // Seating areas (benches)
    for (let i = 0; i < 6; i++) {
      const bx = tg.x + m(12) + i * m(12);
      const bz = tg.z + tg.d / 2;
      if (!this._isInsideFootprint(bx, bz)) continue;
      const bench = MeshBuilder.CreateBox(`terrBench_${i}`, {
        width: m(1.5), height: m(0.45), depth: m(0.5),
      }, this.scene);
      bench.position = new Vector3(bx, topY + m(0.52), bz);
      bench.material = this.mats.wood;
    }

    // Yoga area (flat platform)
    const yogaCx = tg.x + tg.w / 2, yogaCz = tg.z + tg.d / 2 - m(8);
    if (this._isInsideFootprint(yogaCx, yogaCz)) {
      const yogaArea = MeshBuilder.CreateBox('yogaArea', {
        width: m(8), height: m(0.05), depth: m(6),
      }, this.scene);
      yogaArea.position = new Vector3(yogaCx, topY + m(0.33), yogaCz);
      yogaArea.material = this.mats.designerTile;
    }
  }

  // ─── HELPERS ───
  _createWallSegment(name, x, y, z, length, height, thickness, direction, material) {
    let w, d;
    let px, pz;

    if (direction === 'EW') {
      w = length; d = thickness;
      px = x + length / 2;
      pz = z;
    } else {
      w = thickness; d = length;
      px = x;
      pz = z + length / 2;
    }

    const mesh = MeshBuilder.CreateBox(name, { width: w, height, depth: d }, this.scene);
    mesh.position = new Vector3(px, y + height / 2, pz);
    mesh.material = material;
    return mesh;
  }

  _selectUnit(mesh) {
    // Clear previous
    if (this.selectedMesh) {
      this.highlightLayer.removeMesh(this.selectedMesh);
    }
    this.selectedMesh = mesh;
    this.highlightLayer.addMesh(mesh, Color3.FromHexString('#c9a94e'));

    if (this.onUnitSelect && mesh.metadata) {
      this.onUnitSelect(mesh.metadata);
    }
  }

  // ─── REBUILD EXTERIOR (dispose + recreate parking, planters, ground pad, road features) ───
  rebuildExterior() {
    // Dispose all exterior meshes by name prefix (including merged meshes)
    const prefixes = [
      'groundPad', 'parkGrp_', 'pLine_', 'planter_', 'planterGr_', 'planterTr_',
      'gapPl_', 'gapGr_', 'gapTr', 'stairPl_', 'stairGr_', 'stairTr_',
      'roadPl_', 'roadPlGr_', 'roadTr_', 'roadWalkway', 'roadCurb',
      'placedRoad_', 'placedRoadLine_', 'roadMedPl_', 'roadMedGr_', 'roadMedTr_',
      // Merged mesh names
      'surfaceParkLines_merged', 'roadDashes_merged', 'soilBoxes_merged',
      'treeBases_merged', 'sidewalks_merged',
    ];
    const toDispose = this.scene.meshes.filter(m =>
      prefixes.some(p => m.name.startsWith(p))
    );
    toDispose.forEach(m => m.dispose());

    // Clear cached diagonal geometry (stair outline may have changed)
    this._diagGeom = null;

    // Recreate
    this._createGroundPad();
    this._createSurfaceParking();
    this._createPlacedPlanters();
    this._createPlacedRoads();

    // Re-freeze new meshes
    this.scene.meshes.forEach(m => {
      if (m.name.includes('_click')) return;
      if (m.parent && m.parent.name && m.parent.name.startsWith('floor_')) return;
      if (!m.isWorldMatrixFrozen) {
        m.freezeWorldMatrix();
        m.doNotSyncBoundingInfo = true;
      }
    });

    // Re-merge exterior groups
    this._mergeStaticMeshes();
  }

  // ─── FLOOR VISIBILITY ───
  showFloor(floorKey) {
    const selectedNum = parseInt(floorKey);
    const selectedData = BUILDING.floors[floorKey];
    const cutoffY = selectedData
      ? selectedData.elevation + selectedData.height
      : 999;

    // 1. Floor nodes: hide above, show selected + below
    Object.entries(this.floorNodes).forEach(([key, node]) => {
      const keyNum = parseInt(key);
      if (keyNum > selectedNum) {
        // Fully hide floors above
        node.setEnabled(false);
      } else if (keyNum === selectedNum) {
        node.setEnabled(true);
        node.getChildMeshes().forEach(m => {
          // Hide ceiling meshes so interior is visible from top
          if (m.name.includes('_ceil')) {
            m.visibility = 0;
          } else {
            m.visibility = 1.0;
          }
        });
      } else {
        node.setEnabled(true);
        node.getChildMeshes().forEach(m => {
          m.visibility = 1.0;
        });
      }
    });

    // 2. Hide roof signage TransformNode
    const signRoot = this.scene.getTransformNodeByName('signRoot');
    if (signRoot) signRoot.setEnabled(false);

    // 3. Hide scene-level meshes above the selected floor
    //    Skip: ground plane, roads, skyDome, floor-node children (already handled)
    const keepNames = new Set(['ground', 'skyDome', 'renderCanvas']);
    this.scene.meshes.forEach(m => {
      // Skip floor-node children (handled in step 1)
      if (m.parent && m.parent.name && m.parent.name.startsWith('floor_')) return;
      // Skip ground, sky, roads, sidewalk-level elements
      if (keepNames.has(m.name)) return;
      if (m.name.startsWith('road_') || m.name === 'groundPlane') return;
      // Skip non-positioned meshes (infinite distance skybox etc.)
      if (m.infiniteDistance) return;

      // Per-floor facade glass and spandrels — hide above selected floor
      const facadeMatch = m.name.match(/^facade_\w+_(-?\d+)/);
      if (facadeMatch) {
        const facadeFloor = parseInt(facadeMatch[1]);
        m.setEnabled(facadeFloor <= selectedNum);
        return;
      }

      // Any mesh above the selected floor's top → hide
      if (m.position && m.position.y > cutoffY + 0.5) {
        m.setEnabled(false);
        return;
      }

      // Merged mullions — hide during floor isolation (spans all floors)
      if (m.name === 'facade_mullions_merged') {
        m.setEnabled(false);
        return;
      }
    });
  }

  showAllFloors() {
    // Restore all floor nodes
    Object.values(this.floorNodes).forEach(node => {
      node.setEnabled(true);
      node.getChildMeshes().forEach(m => {
        m.visibility = 1.0;
      });
    });

    // Restore roof signage TransformNode
    const signRoot = this.scene.getTransformNodeByName('signRoot');
    if (signRoot) signRoot.setEnabled(true);

    // Restore all scene-level meshes
    this.scene.meshes.forEach(m => {
      m.setEnabled(true);
    });
  }
}
